import React, { useState, useEffect, useRef } from 'react';
import { X, Wand2, Download, ExternalLink, Globe, Search, Clock, CheckCircle, Copy, Check, Table, Target, AlertCircle, RotateCcw, RotateCw, Image as ImageIcon, Sparkles, RefreshCw, Undo2, Redo2, BarChart3 } from 'lucide-react';
import { BrandKit, CTAGenerationResult, PositionSpecificCTA } from '../types';
import googleDocsService from '../services/googleDocsService';
import { autoSaveBlogIfReady } from '../services/blogHistoryService';
import { API_ENDPOINTS, buildApiUrl, API_BASE_URL } from '../config/api';
import { makeApiRequest } from '../utils/apiHelpers';
import LinkHoverControls from './LinkHoverControls';
import { useLinkHoverControls } from '../hooks/useLinkHoverControls';
import { GARY_HALBERT_USCN_TONE_TRAINING } from '../config/toneTraining';
import { BLOG_CREATOR_WORKFLOW_STAGES, MODEL_DISPLAY_NAMES } from '../config/aiModels';
import { sanitizeHTMLForDisplay } from '../utils/htmlSanitizer';
import {
  generateQuizCTAs,
  generateQuizUtmUrl,
  getQuizCtaHtml,
  getQuizCtaStyles,
  countQuizCTAs,
  clearQuizCTAs,
  QUIZ_BUTTON_OPTIONS,
} from '../utils/quizCtaGenerator';
import QuizCtaPreviewCards from './QuizCtaPreviewCards';

// Skeleton component for loading states
const Skeleton = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <div 
    className={className}
    style={{
      backgroundColor: '#f3f4f6',
      borderRadius: '0.375rem',
      animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
      ...style
    }}
  />
);

// Import the KeywordRow interface from BlogCreatorPage
interface KeywordRow {
  id: string;
  keyword: string;
  status: 'pending' | 'running' | 'completed' | 'error';
  progress: string;
  output: string;
  createdAt: Date;
  metadata?: {
    title: string;
    description: string;
    metaSeoTitle?: string;
    metaDescription?: string;
    word_count: number;
    seo_optimized: boolean;
    citations_included: boolean;
    brand_variables_processed: number;
    aeo_optimized: boolean;
  };
  generationResult?: any; // Complete API response for debugging/analysis
}

interface BlogContentActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  keywordRow: KeywordRow;
  onContentUpdate?: (keywordId: string, newContent: string) => void;
  onStatusUpdate?: (keywordId: string, status: KeywordRow['status']) => void;
  // When set, image generation uses product-aware GPT-Image-2 prompts (Dr. Mani blogs)
  productImageKey?: 'psb' | 'ckaa' | 'ss';
}

// CTA-related interfaces (copied from CTACreatorPage)
interface CTAVariant {
  position: 'beginning' | 'middle' | 'end';
  cta: {
    category_header: string;
    headline: string;
    description: string;
    action_button: string;
  };
  strategy: string;
  shortcode: string;
}

// CTAGenerationResult interface imported from types

interface GeneratedImage {
  base64Data: string;
  mimeType: string;
  altText: string;
  placement: 'beginning' | 'middle' | 'ending';
  imageUrl?: string;   // File URL for HTML insertion (from backend storage)
  filename?: string;   // Actual filename on disk
}

/**
 * Variables Menu Component
 * Why this matters: Provides a searchable popup positioned relative to the Variables Menu button.
 * Moved outside main component to prevent re-creation on every render and maintain input focus.
 */
const VariablesMenu: React.FC<{
  showVariablesMenu: boolean;
  variablesButtonPosition: { top: number; left: number } | null;
  variablesMenuRef: React.RefObject<HTMLDivElement | null>;
  activePromptField: 'system' | 'user';
  variableSearchTerm: string;
  setVariableSearchTerm: (term: string) => void;
  setShowVariablesMenu: (show: boolean) => void;
  brandKit: BrandKit | null;
  insertVariable: (value: string) => void;
}> = ({
  showVariablesMenu,
  variablesButtonPosition,
  variablesMenuRef,
  activePromptField,
  variableSearchTerm,
  setVariableSearchTerm,
  setShowVariablesMenu,
  brandKit,
  insertVariable
}) => {
  if (!showVariablesMenu || !variablesButtonPosition) return null;

  /**
   * Get all available variables
   * Why this matters: Provides a comprehensive list of brand variables for easy insertion.
   */
  const getAllVariables = () => {
    const standardVariables = [
      { key: 'url', label: 'Brand URL', value: '{{ brand_kit.url }}' },
      { key: 'about_brand', label: 'About Brand', value: '{{ brand_kit.about_brand }}' },
      { key: 'ideal_customer_profile', label: 'Ideal Customer Profile', value: '{{ brand_kit.ideal_customer_profile }}' },
      { key: 'competitors', label: 'Competitors', value: '{{ brand_kit.competitors }}' },
      { key: 'brand_point_of_view', label: 'Brand Point of View', value: '{{ brand_kit.brand_point_of_view }}' },
      { key: 'author_persona', label: 'Author Persona', value: '{{ brand_kit.author_persona }}' },
      { key: 'tone_of_voice', label: 'Tone of Voice', value: '{{ brand_kit.tone_of_voice }}' },
      { key: 'header_case_type', label: 'Header Case Type', value: '{{ brand_kit.header_case_type }}' },
      { key: 'writing_rules', label: 'Writing Rules', value: '{{ brand_kit.writing_rules }}' },
      { key: 'cta_text', label: 'CTA Text', value: '{{ brand_kit.cta_text }}' },
      { key: 'cta_destination', label: 'CTA Destination', value: '{{ brand_kit.cta_destination }}' }
    ];

    const customVariables = brandKit ? Object.keys(brandKit.customVariables).map(key => ({
      key,
      label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value: `{{ brand_kit.${key} }}`
    })) : [];

    return [...customVariables, ...standardVariables];
  };

  /**
   * Filter variables based on search term
   * Why this matters: Makes it easy to find specific variables in a long list.
   */
  const getFilteredVariables = () => {
    const allVariables = getAllVariables();
    if (!variableSearchTerm) return allVariables;
    
    return allVariables.filter(variable => 
      variable.label.toLowerCase().includes(variableSearchTerm.toLowerCase()) ||
      variable.key.toLowerCase().includes(variableSearchTerm.toLowerCase())
    );
  };

  const filteredVariables = getFilteredVariables();

  return (
    <div
      ref={variablesMenuRef}
      className="content-variables-menu"
      style={{
        position: 'fixed',
        top: variablesButtonPosition.top,
        left: variablesButtonPosition.left,
        backgroundColor: 'white',
        border: '0.0625rem solid #e5e7eb',
        borderRadius: '0.75rem',
        boxShadow: '0 1.5625rem 3.125rem -0.75rem rgba(0, 0, 0, 0.25)',
        zIndex: 10000,
        width: '25rem',
        maxHeight: '37.5rem',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <div style={{
        padding: '1.5rem 1.5rem 1rem 1.5rem',
        borderBottom: '0.0625rem solid #f3f4f6',
        backgroundColor: '#fafafa'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0, color: '#111827' }}>
            Brand Variables
          </h3>
          <button
            onClick={() => setShowVariablesMenu(false)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.25rem',
              borderRadius: '0.25rem',
              transition: 'background-color 0.15s ease'
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <X size={20} />
          </button>
        </div>
        <p style={{ color: '#6b7280', margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
          Insert into {activePromptField} prompt
        </p>
      </div>

      {/* Search */}
      <div style={{ padding: '1rem 1.5rem' }}>
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ 
            position: 'absolute', 
            left: '0.75rem', 
            top: '50%', 
            transform: 'translateY(-50%)', 
            color: '#9ca3af',
            pointerEvents: 'none'
          }} />
          <input
            type="text"
            placeholder="Search variables..."
            value={variableSearchTerm}
            onChange={(e) => setVariableSearchTerm(e.target.value)}
            autoComplete="off"
            style={{
              width: '100%',
              padding: '0.625rem 0.625rem 0.625rem 2.5rem',
              border: '0.0625rem solid #e5e7eb',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              backgroundColor: 'white',
              outline: 'none',
              transition: 'border-color 0.2s ease',
              fontFamily: "'ABCDiatype', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
              cursor: 'text',
              color: '#374151'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.backgroundColor = 'white';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.backgroundColor = 'white';
            }}
          />
        </div>
      </div>

      {/* Variables List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 1.5rem 1.5rem 1.5rem' }}>
        {filteredVariables.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {filteredVariables.map((variable) => (
              <div
                key={variable.key}
                onClick={() => insertVariable(variable.value)}
                style={{
                  padding: '1rem',
                  border: '0.0625rem solid #e5e7eb',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  backgroundColor: 'white'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.borderColor = '#3b82f6';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                <div style={{ 
                  fontWeight: '600', 
                  fontSize: '0.875rem', 
                  marginBottom: '0.5rem',
                  color: '#374151'
                }}>
                  {variable.label}
                </div>
                <div style={{ 
                  fontFamily: 'FoundersGroteskMono, monospace', 
                  fontSize: '0.75rem', 
                  color: '#7c3aed',
                  backgroundColor: '#f3f4f6',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.25rem',
                  border: '0.0625rem solid #e5e7eb',
                  fontWeight: '600'
                }}>
                  {variable.value}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ 
            padding: '2rem', 
            textAlign: 'center', 
            color: '#9ca3af' 
          }}>
            <Search size={32} style={{ margin: '0 auto 1rem', opacity: 0.3, display: 'block' }} />
            <p style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.25rem' }}>
              No variables found
            </p>
            <p style={{ fontSize: '0.75rem', margin: 0 }}>
              Try adjusting your search terms
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * BlogContentActionModal Component
 * Why this matters: Provides post-generation actions for Blog Agents content that are identical to ContentCreationModal
 * for consistent UX - edit, publish, copy, and manage generated articles with brand kit integration.
 */
const BlogContentActionModal: React.FC<BlogContentActionModalProps> = ({ 
  isOpen, 
  onClose, 
  keywordRow, 
  onContentUpdate, 
  onStatusUpdate,
  productImageKey
}) => {
  const [systemPrompt, setSystemPrompt] = useState('');
  const [userPrompt, setUserPrompt] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [metaSeoTitle, setMetaSeoTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showMetaTitleCopied, setShowMetaTitleCopied] = useState(false);
  const [showMetaDescCopied, setShowMetaDescCopied] = useState(false);
  const [brandKit, setBrandKit] = useState<BrandKit | null>(null);
  const [showCMSModal, setShowCMSModal] = useState(false);
  const [showVariablesMenu, setShowVariablesMenu] = useState(false);
  const [activePromptField, setActivePromptField] = useState<'system' | 'user'>('system');
  const [variableSearchTerm, setVariableSearchTerm] = useState('');
  const [variablesButtonPosition, setVariablesButtonPosition] = useState<{ top: number; left: number } | null>(null);
  const [generationStep, setGenerationStep] = useState(0);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'saving' | 'saved' | ''>('');
  const [autoSaveTimeout, setAutoSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  const [hasUserInput, setHasUserInput] = useState(false);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const [showComingSoonMessage, setShowComingSoonMessage] = useState<string | null>(null);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [isEditingContent, setIsEditingContent] = useState(false);
  
  // Contextual CTA states
  const [enableContextualCtas, setEnableContextualCtas] = useState(true);
  const [isEnhancingWithCtas, setIsEnhancingWithCtas] = useState(false);
  const [ctaEnhancementResult, setCtaEnhancementResult] = useState<any>(null);

  // Content display ref for link hover controls
  const contentDisplayRef = useRef<HTMLDivElement>(null);
  
  // Link hover controls
  const {
    targetLink,
    isHovering: isHoveringLink,
    handleLinkHover,
    handleLinkLeave,
    removeLink,
    openLink
  } = useLinkHoverControls(contentDisplayRef);
  const [editableContent, setEditableContent] = useState('');
  const [showGeneratedContentModal, setShowGeneratedContentModal] = useState(false);
  
  // Google Sheets related states
  const [isOpeningSheets, setIsOpeningSheets] = useState(false);
  const [showSheetsMessage, setShowSheetsMessage] = useState(false);
  const [sheetsSuccessMessage, setSheetsSuccessMessage] = useState('');

  // Custom CMS Demo States
  const [showCustomCMSForm, setShowCustomCMSForm] = useState<boolean>(false);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [publishResult, setPublishResult] = useState<any>(null);
  const [customCMSConfig, setCustomCMSConfig] = useState({
    api_endpoint: 'https://uscitrusnursery.myshopify.com',
    api_key: 'configured-server-side',
    cms_type: 'shopify'
  });

  // CTA Generation States (copied from CTACreatorPage)
  const [generatedCTAs, setGeneratedCTAs] = useState<CTAGenerationResult | null>(null);
  const [isGeneratingCTAs, setIsGeneratingCTAs] = useState(false);
  const [ctaGenerationStage, setCtaGenerationStage] = useState('');
  const [ctaError, setCtaError] = useState('');
  const [ctaCopySuccess, setCtaCopySuccess] = useState<string>('');
  const [vocKitReady, setVocKitReady] = useState(false);
  const [painPointsCount, setPainPointsCount] = useState(0);
  const [quizVariantIndex, setQuizVariantIndex] = useState(() => {
    try {
      const saved = localStorage.getItem(`quiz_ctas_${keywordRow.id}`);
      if (saved) return JSON.parse(saved).variantIndex ?? 0;
    } catch {}
    return 0;
  });
  const [showCtaSkeletons, setShowCtaSkeletons] = useState(false);
  const [vocKitReadyDismissed, setVocKitReadyDismissed] = useState(false);

  // Image Generation States
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [regeneratingPlacement, setRegeneratingPlacement] = useState<string | null>(null);
  const [imageError, setImageError] = useState('');
  const [imageCopySuccess, setImageCopySuccess] = useState<string>('');
  const [imageDownloadSuccess, setImageDownloadSuccess] = useState<string>('');

  // Infographic Generation States
  const [generatedInfographic, setGeneratedInfographic] = useState<GeneratedImage | null>(null);
  const [isGeneratingInfographic, setIsGeneratingInfographic] = useState(false);
  const [infographicError, setInfographicError] = useState('');
  const [infographicCopySuccess, setInfographicCopySuccess] = useState<string>('');

  // localStorage key for persisting images per keyword
  const imageStorageKey = `citrus-images-${keywordRow.keyword.toLowerCase().replace(/\s+/g, '-')}`;
  const infographicStorageKey = `citrus-infographic-${keywordRow.keyword.toLowerCase().replace(/\s+/g, '-')}`;

  // Approved CTA button options for dynamic switching (copied from CTACreatorPage)
  const approvedCTAButtons = [
    'Find Your Perfect Tree →',
    'Browse Our Trees →',
    'Shop Citrus Trees →',
    'Buy Citrus Trees →',
    'Order Your Tree →',
    'View Our Collection →'
  ];

  // Undo/Redo functionality for edit mode
  const [editHistory, setEditHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [historySaveTimeout, setHistorySaveTimeout] = useState<NodeJS.Timeout | null>(null);
  
  const systemPromptRef = useRef<HTMLTextAreaElement>(null);
  const userPromptRef = useRef<HTMLTextAreaElement>(null);
  const variablesMenuRef = useRef<HTMLDivElement>(null);
  const systemVariablesButtonRef = useRef<HTMLButtonElement>(null);
  const userVariablesButtonRef = useRef<HTMLButtonElement>(null);
  const generationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const editableContentRef = useRef<HTMLTextAreaElement>(null);

  // Generation progress messages for Blog Agents (fallback while polling; backend sends live updates)
  const generationMessages = [...BLOG_CREATOR_WORKFLOW_STAGES];

  /**
   * Generate Images using Gemini (Nano Banana Pro)
   * Why this matters: Creates 3 custom images for the blog post
   */
  const generateImages = async () => {
    setIsGeneratingImages(true);
    setImageError('');
    setGeneratedImages([]); // Clear previous
    
    try {
      const response = productImageKey
        ? await makeApiRequest(API_ENDPOINTS.productArticlesGenerateImages, {
            method: 'POST',
            body: JSON.stringify({ productKey: productImageKey, topicTitle: keywordRow.keyword })
          })
        : await makeApiRequest(API_ENDPOINTS.citrusTreeGenerateImages, {
            method: 'POST',
            body: JSON.stringify({ treeName: keywordRow.keyword })
          });
      
      if (response.success && response.data?.images) {
        setGeneratedImages(response.data.images);
      } else {
        setImageError('Failed to generate images. Please try again.');
      }
    } catch (error: any) {
      console.error('Image generation failed:', error);
      setImageError(error.message || 'Failed to generate images');
    } finally {
      setIsGeneratingImages(false);
    }
  };

  /**
   * Regenerate a single image by placement
   * Why this matters: Allows user to regenerate just one image without regenerating all three
   */
  const regenerateSingleImage = async (placement: 'beginning' | 'middle' | 'ending') => {
    setRegeneratingPlacement(placement);
    setImageError('');
    
    try {
      const response = productImageKey
        ? await makeApiRequest(API_ENDPOINTS.productArticlesGenerateImages, {
            method: 'POST',
            body: JSON.stringify({ productKey: productImageKey, topicTitle: keywordRow.keyword })
          })
        : await makeApiRequest(API_ENDPOINTS.citrusTreeGenerateImages, {
            method: 'POST',
            body: JSON.stringify({ treeName: keywordRow.keyword })
          });
      
      if (response.success && response.data?.images) {
        // Find the new image for the specified placement
        const newImage = response.data.images.find((img: GeneratedImage) => img.placement === placement);
        
        if (newImage) {
          // Update only the specific image in state
          setGeneratedImages(prev => 
            prev.map(img => img.placement === placement ? newImage : img)
          );
        }
      } else {
        setImageError(`Failed to regenerate ${placement} image. Please try again.`);
      }
    } catch (error: any) {
      console.error(`Image regeneration failed for ${placement}:`, error);
      setImageError(error.message || `Failed to regenerate ${placement} image`);
    } finally {
      setRegeneratingPlacement(null);
    }
  };

  /**
   * Copy Image HTML to clipboard
   * Why this matters: Allows user to manually insert images into the editor
   * Now uses file URLs when available for cleaner HTML output.
   */
  const copyImageHtml = (image: GeneratedImage) => {
    // Prefer file URL over base64 for cleaner, more portable HTML
    // Check if URL is already absolute (Vercel Blob) or needs API_BASE_URL prepended
    const imageSrc = image.imageUrl 
      ? (image.imageUrl.startsWith('http') ? image.imageUrl : `${API_BASE_URL}${image.imageUrl}`)
      : `data:${image.mimeType};base64,${image.base64Data}`;
    
    const html = `<figure style="margin: 2rem 0; text-align: center;">
  <img src="${imageSrc}" alt="${image.altText}" style="max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);" />
  <figcaption style="margin-top: 0.75rem; font-size: 0.9rem; color: #666; font-style: italic;">${image.altText}</figcaption>
</figure>`;
    
    navigator.clipboard.writeText(html).then(() => {
      setImageCopySuccess(image.placement);
      setTimeout(() => setImageCopySuccess(''), 2000);
    });
  };

  /**
   * Download a generated image/infographic as a PNG file.
   * Why this matters: lets the user save the asset locally instead of only
   * inserting/copying HTML. Works for both cloud URLs (fetch to blob) and inline
   * base64; falls back to opening the URL if a cross-origin fetch is blocked.
   */
  const downloadImageAsPng = async (image: GeneratedImage) => {
    const slug = (keywordRow.keyword || 'image')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);
    const filename = `${slug || 'image'}-${image.placement}.png`;

    try {
      let blob: Blob;
      if (image.imageUrl) {
        const src = image.imageUrl.startsWith('http')
          ? image.imageUrl
          : `${API_BASE_URL}${image.imageUrl}`;
        const resp = await fetch(src);
        if (!resp.ok) throw new Error(`Fetch failed: ${resp.status}`);
        blob = await resp.blob();
      } else if (image.base64Data) {
        const byteChars = atob(image.base64Data);
        const bytes = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
        blob = new Blob([bytes], { type: image.mimeType || 'image/png' });
      } else {
        throw new Error('No image data to download');
      }

      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);

      setImageDownloadSuccess(image.placement);
      setTimeout(() => setImageDownloadSuccess(''), 2000);
    } catch (err) {
      console.error('Image download failed, opening in new tab as fallback:', err);
      if (image.imageUrl) {
        const src = image.imageUrl.startsWith('http')
          ? image.imageUrl
          : `${API_BASE_URL}${image.imageUrl}`;
        window.open(src, '_blank');
      }
    }
  };

  /**
   * Insert Image HTML at cursor position in editor
   * Why this matters: Allows direct insertion of generated images into content at cursor position, 
   * same workflow as CTA insertion for consistency.
   * 
   * Now uses file URLs (when available) instead of base64 data URIs for cleaner HTML
   * and better compatibility with CMS/HTML editors.
   */
  const insertImageAtCursor = (image: GeneratedImage) => {
    if (!editableContentRef.current || !isEditingContent) {
      // If not in edit mode, show a message
      setImageError('Please enter Edit Mode first, then click where you want to insert the image.');
      setTimeout(() => setImageError(''), 3000);
      return;
    }

    const textarea = editableContentRef.current;
    const cursorPosition = textarea.selectionStart;
    
    // Capture scroll position before making changes to preserve user's view
    const scrollTop = textarea.scrollTop;
    const scrollLeft = textarea.scrollLeft;
    
    // Determine the image source - prefer file URL over base64 data URI
    // Why this matters: File URLs result in cleaner HTML and better compatibility
    // with CMS systems and HTML editors that may struggle with large base64 strings
    let imageSrc: string;
    if (image.imageUrl) {
      // Use the file URL - check if already absolute (Vercel Blob) or needs API_BASE_URL
      imageSrc = image.imageUrl.startsWith('http') ? image.imageUrl : `${API_BASE_URL}${image.imageUrl}`;
      console.log(`🖼️ Using file URL for ${image.placement} image: ${imageSrc}`);
    } else {
      // Fallback to base64 data URI
      imageSrc = `data:${image.mimeType};base64,${image.base64Data}`;
      console.log(`🖼️ Using base64 fallback for ${image.placement} image`);
    }
    
    // Create the image HTML with proper source
    const imageHtml = `<figure style="margin: 2rem 0; text-align: center;">
  <img src="${imageSrc}" alt="${image.altText}" style="max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);" />
  <figcaption style="margin-top: 0.75rem; font-size: 0.9rem; color: #666; font-style: italic;">${image.altText}</figcaption>
</figure>`;
    
    const beforeCursor = editableContent.substring(0, cursorPosition);
    const afterCursor = editableContent.substring(cursorPosition);
    
    // Add spacing around the image for better readability
    const imageWithComment = `\n\n<!-- Image: ${image.placement} -->\n${imageHtml}\n<!-- End Image -->\n\n`;
    
    const newContent = beforeCursor + imageWithComment + afterCursor;
    
    // Save to history and update content
    saveToHistory(newContent);
    setEditableContent(newContent);
    
    // Focus back to textarea and position cursor after the inserted image
    setTimeout(() => {
      const newCursorPosition = cursorPosition + imageWithComment.length;
      
      textarea.focus();
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
      
      // Restore scroll position
      textarea.scrollTop = scrollTop;
      textarea.scrollLeft = scrollLeft;
      
      console.log(`📍 Image cursor positioned at ${newCursorPosition}, scroll restored to ${scrollTop}`);
    }, 50);

    // Show success feedback
    setImageCopySuccess(`${image.placement}_inserted`);
    setTimeout(() => setImageCopySuccess(''), 2000);

    console.log(`🖼️ Inserted ${image.placement} image at cursor position ${cursorPosition}`);
  };

  /**
   * Generate Infographic using Gemini (Nano Banana Pro)
   * Why this matters: Creates a branded infographic summarizing the article
   */
  const generateInfographic = async () => {
    setIsGeneratingInfographic(true);
    setInfographicError('');
    setGeneratedInfographic(null); // Clear previous
    
    try {
      const response = productImageKey
        ? await makeApiRequest(API_ENDPOINTS.productArticlesGenerateInfographic, {
            method: 'POST',
            body: JSON.stringify({
              productKey: productImageKey,
              topicTitle: keywordRow.keyword,
              // Send the article so the infographic summarizes THIS content
              content: generatedContent || keywordRow.output || ''
            })
          })
        : await makeApiRequest(API_ENDPOINTS.citrusTreeGenerateInfographic, {
            method: 'POST',
            body: JSON.stringify({ treeName: keywordRow.keyword })
          });
      
      if (response.success && response.data?.infographic) {
        setGeneratedInfographic(response.data.infographic);
        // Note: localStorage persistence is handled by useEffect (saves only URL, not base64)
      } else {
        setInfographicError('Failed to generate infographic. Please try again.');
      }
    } catch (error: any) {
      console.error('Infographic generation failed:', error);
      setInfographicError(error.message || 'Failed to generate infographic');
    } finally {
      setIsGeneratingInfographic(false);
    }
  };

  /**
   * Copy Infographic HTML to clipboard
   * Why this matters: Allows user to manually insert infographic into the editor
   */
  const copyInfographicHtml = () => {
    if (!generatedInfographic) return;
    
    const imageSrc = generatedInfographic.imageUrl 
      ? (generatedInfographic.imageUrl.startsWith('http') ? generatedInfographic.imageUrl : `${API_BASE_URL}${generatedInfographic.imageUrl}`)
      : `data:${generatedInfographic.mimeType};base64,${generatedInfographic.base64Data}`;
    
    const html = `<figure style="margin: 2rem 0; text-align: center;">
  <img src="${imageSrc}" alt="${generatedInfographic.altText}" style="max-width: 100%; height: auto; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);" />
  <figcaption style="margin-top: 0.75rem; font-size: 0.9rem; color: #666; font-style: italic;">${generatedInfographic.altText}</figcaption>
</figure>`;
    
    navigator.clipboard.writeText(html).then(() => {
      setInfographicCopySuccess('copied');
      setTimeout(() => setInfographicCopySuccess(''), 2000);
    });
  };

  /**
   * Insert Infographic HTML at cursor position in editor
   * Why this matters: Allows direct insertion of infographic into content at cursor position
   */
  const insertInfographicAtCursor = () => {
    if (!generatedInfographic) return;
    
    if (!editableContentRef.current || !isEditingContent) {
      setInfographicError('Please enter Edit Mode first, then click where you want to insert the infographic.');
      setTimeout(() => setInfographicError(''), 3000);
      return;
    }

    const textarea = editableContentRef.current;
    const cursorPosition = textarea.selectionStart;
    
    const scrollTop = textarea.scrollTop;
    const scrollLeft = textarea.scrollLeft;
    
    let imageSrc: string;
    if (generatedInfographic.imageUrl) {
      imageSrc = generatedInfographic.imageUrl.startsWith('http') ? generatedInfographic.imageUrl : `${API_BASE_URL}${generatedInfographic.imageUrl}`;
    } else {
      imageSrc = `data:${generatedInfographic.mimeType};base64,${generatedInfographic.base64Data}`;
    }
    
    const infographicHtml = `<figure style="margin: 2rem 0; text-align: center;">
  <img src="${imageSrc}" alt="${generatedInfographic.altText}" style="max-width: 100%; height: auto; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);" />
  <figcaption style="margin-top: 0.75rem; font-size: 0.9rem; color: #666; font-style: italic;">${generatedInfographic.altText}</figcaption>
</figure>`;
    
    const beforeCursor = editableContent.substring(0, cursorPosition);
    const afterCursor = editableContent.substring(cursorPosition);
    
    const infographicWithComment = `\n\n<!-- Infographic -->\n${infographicHtml}\n<!-- End Infographic -->\n\n`;
    
    const newContent = beforeCursor + infographicWithComment + afterCursor;
    
    saveToHistory(newContent);
    setEditableContent(newContent);
    
    setTimeout(() => {
      const newCursorPosition = cursorPosition + infographicWithComment.length;
      textarea.focus();
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
      textarea.scrollTop = scrollTop;
      textarea.scrollLeft = scrollLeft;
    }, 50);

    setInfographicCopySuccess('inserted');
    setTimeout(() => setInfographicCopySuccess(''), 2000);
    console.log(`📊 Inserted infographic at cursor position ${cursorPosition}`);
  };

  // Add CSS for textarea styling and generated content formatting
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .content-creation-textarea {
        font-family: inherit;
        line-height: 1.5;
      }
      
      .content-creation-textarea::placeholder {
        color: #9ca3af;
      }
      
      .content-creation-textarea:focus {
        outline: none;
        border-color: #3b82f6 !important;
      }

      .generated-content-display {
        font-family: 'ABCDiatype', 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        line-height: 1.6;
        color: #374151;
      }

      .content-modal-btn,
      button {
        font-family: 'ABCDiatype', 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
      }

      .generated-content-display h1 {
        font-family: 'FoundersGrotesk', Arial, sans-serif;
        font-size: 2rem;
        font-weight: 700;
        color: #111827;
        margin: 0 0 1.5rem 0;
        line-height: 1.2;
        border-bottom: 0.125rem solid #f3f4f6;
        padding-bottom: 0.75rem;
      }

      .generated-content-display h2 {
        font-family: 'FoundersGrotesk', Arial, sans-serif;
        font-size: 1.5rem;
        font-weight: 600;
        color: #1f2937;
        margin: 2rem 0 1rem 0;
        line-height: 1.3;
      }

      .generated-content-display h3 {
        font-family: 'FoundersGrotesk', Arial, sans-serif;
        font-size: 1.25rem;
        font-weight: 600;
        color: #374151;
        margin: 1.5rem 0 0.75rem 0;
        line-height: 1.4;
      }

      .generated-content-display p {
        margin: 0 0 1.25rem 0;
        line-height: 1.7;
      }

      .generated-content-display ul, 
      .generated-content-display ol {
        margin: 1rem 0;
        padding-left: 1.5rem;
      }

      .generated-content-display li {
        margin: 0.5rem 0;
        line-height: 1.6;
      }

      .generated-content-display strong {
        font-weight: 600;
        color: #111827;
      }

      .generated-content-display table {
        width: 100%;
        border-collapse: collapse;
        margin: 1.5rem 0;
        border: 1px solid #e5e7eb;
        border-radius: 0.5rem;
        overflow: hidden;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .generated-content-display thead {
        background-color: #f9fafb;
      }

      .generated-content-display th {
        padding: 0.875rem 1rem;
        text-align: left;
        font-weight: 600;
        color: #374151;
        border-bottom: 2px solid #e5e7eb;
        font-size: 0.875rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .generated-content-display td {
        padding: 0.875rem 1rem;
        border-bottom: 1px solid #f3f4f6;
        color: #6b7280;
        font-size: 0.875rem;
        line-height: 1.5;
      }

      .generated-content-display tbody tr:hover {
        background-color: #f9fafb;
      }

      .generated-content-display tbody tr:last-child td {
        border-bottom: none;
      }

      .generated-content-display th:first-child,
      .generated-content-display td:first-child {
        padding-left: 1.25rem;
      }

      .generated-content-display th:last-child,
      .generated-content-display td:last-child {
        padding-right: 1.25rem;
      }

      .generated-content-display a {
        color: #2563eb;
        text-decoration: underline;
        font-weight: 500;
      }

      .generated-content-display a:hover {
        color: #1d4ed8;
        text-decoration: underline;
      }

      /* Mobile backdrop for modals */
      .mobile-modal-backdrop {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        background-color: rgba(0, 0, 0, 0.5) !important;
        z-index: 9999 !important;
      }

      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      
      @keyframes pulse {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0.5;
        }
      }
      
      @keyframes shimmer {
        0% {
          background-position: -200% 0;
        }
        100% {
          background-position: 200% 0;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  /**
   * Close variables menu when clicking outside
   * Why this matters: Provides intuitive UX for the dropdown menu.
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (variablesMenuRef.current && !variablesMenuRef.current.contains(event.target as Node)) {
        setShowVariablesMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Extract meta fields from malformed JSON response
   * Why this matters: Handles cases where the AI response contains the JSON structure but isn't properly formatted
   */
  const extractFieldsFromMalformedJSON = (responseText: string): { content: string; metaSeoTitle: string; metaDescription: string } => {
    console.log('🔧 Attempting field extraction from malformed JSON');
    
    let content = '';
    let metaSeoTitle = '';
    let metaDescription = '';
    
    try {
      // Extract content field - look for "content": and find the value until the next field
      const contentMatch = responseText.match(/"content"\s*:\s*"([\s\S]*?)"\s*,\s*"(?:metaSeoTitle|metaDescription)"/);
      if (contentMatch) {
        content = contentMatch[1]
          .replace(/\\"/g, '"')  // Unescape quotes
          .replace(/\\n/g, '\n') // Unescape newlines
          .replace(/\\t/g, '\t') // Unescape tabs
          .replace(/\\\\/g, '\\'); // Unescape backslashes
        console.log('✅ Extracted content field');
      }
      
      // Extract metaSeoTitle field
      const titleMatch = responseText.match(/"metaSeoTitle"\s*:\s*"([^"]*?)"/);
      if (titleMatch) {
        metaSeoTitle = titleMatch[1];
        console.log('✅ Extracted metaSeoTitle field:', metaSeoTitle);
      }
      
      // Extract metaDescription field
      const descMatch = responseText.match(/"metaDescription"\s*:\s*"([^"]*?)"/);
      if (descMatch) {
        metaDescription = descMatch[1];
        console.log('✅ Extracted metaDescription field:', metaDescription);
      }
      
    } catch (error) {
      console.log('❌ Field extraction failed:', error);
    }
    
    return { content, metaSeoTitle, metaDescription };
  };



  /**
   * Clean AI-generated content by removing unwanted commentary and formatting
   * Why this matters: Ensures clean HTML output by stripping AI meta-commentary and converting markdown to HTML.
   */
  const cleanAIContent = (content: string): string => {
    let cleaned = content;
    
    // Remove markdown code block indicators (```json, """, etc.)
    cleaned = cleaned.replace(/^```json\s*/i, '');
    cleaned = cleaned.replace(/^```\s*/gm, '');
    cleaned = cleaned.replace(/\s*```\s*$/gm, '');
    cleaned = cleaned.replace(/^"""\s*json\s*/i, '');
    cleaned = cleaned.replace(/^"""\s*/gm, '');
    cleaned = cleaned.replace(/\s*"""\s*$/gm, '');
    
    // Remove literal JSON wrapper at the very start: { "content": "
    cleaned = cleaned.replace(/^\s*\{\s*"content"\s*:\s*"/i, '');
    
    // Remove JSON metadata that might be mixed in with content
    cleaned = cleaned.replace(/\{\s*"metaSeoTitle"\s*:\s*"[^"]*"\s*,[\s\S]*?\}/g, '');
    cleaned = cleaned.replace(/\{\s*"content"\s*:\s*"[\s\S]*?"metaSeoTitle"[\s\S]*?\}/g, '');
    
    // Remove literal JSON structure that appears as text content
    cleaned = cleaned.replace(/^\s*\{\s*$/gm, '');
    cleaned = cleaned.replace(/^\s*"content":\s*"?\s*$/gm, '');
    cleaned = cleaned.replace(/^\s*\}\s*$/gm, '');
    cleaned = cleaned.replace(/^[\s\n]*\{[\s\n]*"content":\s*"[\s\n]*/i, '');
    cleaned = cleaned.replace(/[\s\n]*"\s*\}[\s\n]*$/i, '');
    
    // Handle the specific pattern: { "content": " at the start
    cleaned = cleaned.replace(/^\s*\{\s*\n?\s*"content":\s*"\s*\n?\s*/i, '');
    // Handle trailing patterns
    cleaned = cleaned.replace(/\s*"\s*,?\s*"metaSeoTitle"[\s\S]*$/i, '');
    cleaned = cleaned.replace(/\s*"\s*\}\s*$/i, '');
    
    // Clean up literal escape sequences that appear as text (\\n instead of actual newlines)
    cleaned = cleaned.replace(/^\s*\\n\\n\s*/g, '');
    cleaned = cleaned.replace(/\s*\\n\\n\s*$/g, '');
    cleaned = cleaned.replace(/\\n\\n/g, '\n\n');
    cleaned = cleaned.replace(/\\n/g, '\n');
    cleaned = cleaned.replace(/\\t/g, '\t');
    cleaned = cleaned.replace(/\\"/g, '"');
    
    // Reduce excessive bold formatting - keep only strategic emphasis
    // Remove bold from entire sentences (more than 8 words)
    cleaned = cleaned.replace(/<strong>([^<]{60,}?)<\/strong>/g, '$1');
    // Remove bold from percentage/number + text combinations that are too long
    cleaned = cleaned.replace(/<strong>(\d+%?\s+[^<]{20,}?)<\/strong>/g, '<strong>$1</strong>'.replace(/<strong>(\d+%?\s+\w+\s+\w+)\s+([^<]+)<\/strong>/, '<strong>$1</strong> $2'));
    // Keep bold only for short key terms (under 4 words)
    cleaned = cleaned.replace(/<strong>([^<]*?\s+[^<]*?\s+[^<]*?\s+[^<]*?\s+[^<]+?)<\/strong>/g, (match, content) => {
      const words = content.trim().split(/\s+/);
      if (words.length <= 3) return match; // Keep short phrases
      if (/^\d+%/.test(content.trim())) return match; // Keep percentages
      return content; // Remove bold from longer phrases
    });
    
    // Remove common AI introductory phrases
    cleaned = cleaned.replace(/^.*?Here's the.*?content.*?:?\s*/i, '');
    cleaned = cleaned.replace(/^.*?Here's an.*?optimized.*?:?\s*/i, '');
    cleaned = cleaned.replace(/^.*?I'll create.*?:?\s*/i, '');
    cleaned = cleaned.replace(/^.*?Based on.*?analysis.*?:?\s*/i, '');
    
    // Remove markdown code blocks
    cleaned = cleaned.replace(/```html\s*/gi, '');
    cleaned = cleaned.replace(/```\s*/g, '');
    
    // Convert markdown headers to HTML headers
    // Why this matters: AI sometimes returns markdown instead of HTML despite instructions
    cleaned = cleaned.replace(/^### (.+)$/gm, '<h3>$1</h3>');
    cleaned = cleaned.replace(/^## (.+)$/gm, '<h2>$1</h2>');
    cleaned = cleaned.replace(/^# (.+)$/gm, '<h1>$1</h1>');
    
    // Convert markdown bold and italic to HTML
    cleaned = cleaned.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    cleaned = cleaned.replace(/\*(.+?)\*/g, '<em>$1</em>');
    
    // Convert markdown lists to HTML lists
    // Handle unordered lists (- or *)
    cleaned = cleaned.replace(/(?:^|\n)([*-]\s+.+(?:\n[*-]\s+.+)*)/gm, (match) => {
      const items = match.trim().split('\n').map(line => {
        const item = line.replace(/^[*-]\s+/, '').trim();
        return `  <li>${item}</li>`;
      }).join('\n');
      return `<ul>\n${items}\n</ul>`;
    });
    
    // Handle ordered lists (1. 2. etc.)
    cleaned = cleaned.replace(/(?:^|\n)(\d+\.\s+.+(?:\n\d+\.\s+.+)*)/gm, (match) => {
      const items = match.trim().split('\n').map(line => {
        const item = line.replace(/^\d+\.\s+/, '').trim();
        return `  <li>${item}</li>`;
      }).join('\n');
      return `<ol>\n${items}\n</ol>`;
    });
    
    // Remove markdown checkbox syntax [ ] from content (AI tell)
    // Why this matters: Markdown checkboxes are an AI tell that don't render properly in HTML
    // Replace [ ] or [x] at the start of lines (common in checklists)
    cleaned = cleaned.replace(/^\s*\[\s*\]\s*/gm, ''); // Remove [ ] at start of lines
    cleaned = cleaned.replace(/^\s*\[\s*x\s*\]\s*/gmi, ''); // Remove [x] at start of lines
    // Also remove them inline if they appear mid-text
    cleaned = cleaned.replace(/\[\s*\]\s+/g, ''); // Remove [ ] followed by space
    cleaned = cleaned.replace(/\[\s*x\s*\]\s+/gi, ''); // Remove [x] followed by space
    
    // Convert remaining plain text paragraphs to HTML paragraphs
    // Split by double newlines and wrap non-HTML content in <p> tags
    const lines = cleaned.split(/\n\s*\n/);
    cleaned = lines.map(line => {
      const trimmed = line.trim();
      if (!trimmed) return '';
      
      // Skip if already HTML (contains < and >)
      if (trimmed.includes('<') && trimmed.includes('>')) {
        return trimmed;
      }
      
      // Skip if it's just a single word or very short
      if (trimmed.length < 3) return trimmed;
      
      // Wrap in paragraph tags
      return `<p>${trimmed}</p>`;
    }).join('\n\n');
    
    // Remove explanatory text at the end (typically starts with patterns like "This content structure:")
    cleaned = cleaned.replace(/\n\s*This content structure:[\s\S]*$/i, '');
    cleaned = cleaned.replace(/\n\s*Would you like me to[\s\S]*$/i, '');
    cleaned = cleaned.replace(/\n\s*The content includes:[\s\S]*$/i, '');
    cleaned = cleaned.replace(/\n\s*Key features of this content:[\s\S]*$/i, '');
    
    // Remove numbered analysis points at the end
    cleaned = cleaned.replace(/\n\s*\d+\.\s+[A-Z][^<\n]*[\s\S]*$/i, '');
    
    // Format email templates better - add line breaks after sentences in template sections
    cleaned = cleaned.replace(/(Template \d+:.*?<\/p>|<p>.*?Template.*?<\/p>)/gi, (match) => {
      // Add line breaks after periods followed by space and capital letter (sentence boundaries)
      return match.replace(/\.\s+([A-Z])/g, '.<br><br>$1');
    });
    
    // Add line breaks after common email elements
    cleaned = cleaned.replace(/(Subject:.*?)([A-Z][a-z])/g, '$1<br><br>$2');
    cleaned = cleaned.replace(/(Hi \{\{.*?\}\},)\s*([A-Z])/g, '$1<br><br>$2');
    cleaned = cleaned.replace(/(Best regards,|Best,|Sincerely,)\s*([A-Z\[])/g, '$1<br><br>$2');
    
    // If content starts with HTML, ensure it starts with a proper tag
    if (cleaned.includes('<') && !cleaned.trim().startsWith('<')) {
      const htmlStart = cleaned.indexOf('<');
      if (htmlStart > 0) {
        cleaned = cleaned.substring(htmlStart);
      }
    }
    
    // Trim whitespace and clean up extra newlines
    cleaned = cleaned.trim().replace(/\n{3,}/g, '\n\n');
    
    return cleaned;
  };

  /**
   * Parse AI response and extract JSON fields
   * Why this matters: Properly extracts content, metaSeoTitle, and metaDescription from AI JSON response.
   */
  const parseAIResponse = (responseText: string): { content: string; metaSeoTitle: string; metaDescription: string } => {
    // Add detailed logging for debugging
    console.log('🔍 Raw AI Response Length:', responseText.length);
    console.log('🔍 Raw AI Response Preview:', responseText.substring(0, 200) + '...');
    console.log('🔍 Raw AI Response End:', '...' + responseText.substring(responseText.length - 200));
    
    try {
      // Clean the response text first
      let cleanedResponse = responseText.trim();
      
      // Remove any markdown code blocks that might be wrapping the JSON
      cleanedResponse = cleanedResponse.replace(/^```json\s*/i, '');
      cleanedResponse = cleanedResponse.replace(/\s*```$/i, '');
      cleanedResponse = cleanedResponse.replace(/^```\s*/i, '');
      
      console.log('🔍 Cleaned Response Length:', cleanedResponse.length);
      console.log('🔍 Cleaned Response Preview:', cleanedResponse.substring(0, 200) + '...');
      
      // First, try to parse the entire response as JSON
      let parsed;
      try {
        parsed = JSON.parse(cleanedResponse);
        console.log('✅ Successfully parsed entire response as JSON');
      } catch (parseError) {
        console.log('❌ Failed to parse entire response as JSON:', parseError);
        
        // Try to find JSON object boundaries more carefully
        let jsonStart = cleanedResponse.indexOf('{');
        let jsonEnd = cleanedResponse.lastIndexOf('}');
        
        if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
          const potentialJson = cleanedResponse.substring(jsonStart, jsonEnd + 1);
          console.log('🔍 Extracted potential JSON:', potentialJson.substring(0, 200) + '...');
          
          try {
            parsed = JSON.parse(potentialJson);
            console.log('✅ Successfully parsed extracted JSON');
          } catch (extractError) {
            console.log('❌ Failed to parse extracted JSON:', extractError);
            
            // Try more aggressive pattern matching
            const jsonMatches = cleanedResponse.match(/\{[^{}]*"content"[^{}]*"metaSeoTitle"[^{}]*"metaDescription"[^{}]*\}/g) ||
                               cleanedResponse.match(/\{[^{}]*"metaSeoTitle"[^{}]*"metaDescription"[^{}]*"content"[^{}]*\}/g) ||
                               cleanedResponse.match(/\{[\s\S]*?\}/g);
            
            console.log('🔍 Pattern matches found:', jsonMatches?.length || 0);
            
            if (jsonMatches && jsonMatches.length > 0) {
              // Try the longest match first (most likely to be complete)
              const sortedMatches = jsonMatches.sort((a, b) => b.length - a.length);
              
              for (let i = 0; i < sortedMatches.length; i++) {
                const match = sortedMatches[i];
                console.log(`🔍 Trying match ${i + 1}:`, match.substring(0, 100) + '...');
                
                try {
                  parsed = JSON.parse(match);
                  if (parsed && typeof parsed === 'object' && (parsed.content || parsed.metaSeoTitle || parsed.metaDescription)) {
                    console.log('✅ Successfully parsed match', i + 1);
                    break;
                  }
                } catch (matchError) {
                  console.log(`❌ Failed to parse match ${i + 1}:`, matchError);
                  
                  // Try individual field extraction as final fallback
                  const extractedFields = extractFieldsFromMalformedJSON(cleanedResponse);
                  if (extractedFields.content || extractedFields.metaSeoTitle || extractedFields.metaDescription) {
                    console.log('✅ Successfully extracted fields from malformed JSON');
                    parsed = extractedFields;
                    break;
                  }
                  continue;
                }
              }
            }
          }
        }
      }

      // Check if we have the expected JSON structure
      if (parsed && typeof parsed === 'object') {
        const hasContent = parsed.content && typeof parsed.content === 'string';
        const hasTitle = parsed.metaSeoTitle && typeof parsed.metaSeoTitle === 'string';
        const hasDescription = parsed.metaDescription && typeof parsed.metaDescription === 'string';
        
        console.log('🔍 Parsed object structure:', {
          hasContent,
          hasTitle,
          hasDescription,
          keys: Object.keys(parsed)
        });
        
        if (hasContent || hasTitle || hasDescription) {
          console.log('✅ Successfully parsed JSON response:', {
            hasContent,
            hasTitle,
            hasDescription,
            contentLength: hasContent ? parsed.content.length : 0,
            titleLength: hasTitle ? parsed.metaSeoTitle.length : 0,
            descLength: hasDescription ? parsed.metaDescription.length : 0
          });
          
          return {
            content: hasContent ? cleanAIContent(parsed.content) : cleanAIContent(responseText),
            metaSeoTitle: hasTitle ? parsed.metaSeoTitle : '', // Let AI generate proper length titles
            metaDescription: hasDescription ? parsed.metaDescription : '' // Show full AI-generated description
          };
        } else {
          console.log('❌ Parsed object missing required fields');
        }
      } else {
        console.log('❌ Failed to get valid parsed object');
      }
    } catch (error) {
      console.log('❌ Unexpected error in parseAIResponse:', error);
    }

    console.log('⚠️ Falling back to legacy content parsing');
    
    // Try to extract meta fields from the raw text if they exist
    let extractedTitle = '';
    let extractedDescription = '';
    
    // Look for patterns like "metaSeoTitle": "..."
    const titleMatch = responseText.match(/"metaSeoTitle"\s*:\s*"([^"]+)"/);
    const descMatch = responseText.match(/"metaDescription"\s*:\s*"([^"]+)"/);
    
    if (titleMatch) {
      extractedTitle = titleMatch[1]; // Let AI generate proper length titles
      console.log('🔍 Extracted title via regex:', extractedTitle);
    }
    
    if (descMatch) {
      extractedDescription = descMatch[1];
      console.log('🔍 Extracted description via regex:', extractedDescription);
    }
    
    // Fallback to legacy content cleaning
    const cleanedContent = cleanAIContent(responseText);
    console.log('🔍 Cleaned content length:', cleanedContent.length);
    
    return {
      content: cleanedContent,
      metaSeoTitle: extractedTitle,
      metaDescription: extractedDescription
    };
  };

  // Initialize content from keywordRow when modal opens
  useEffect(() => {
    if (!isOpen || !keywordRow) return;
    
    // Load saved data first
    loadSavedData();
    
    // Always prioritize fresh content from keywordRow over cached content
    // This ensures users see the latest generated content with proper US Citrus branding
    const content = keywordRow.output || '';
    if (content.trim().length > 0) {
      // Clean the content to remove JSON artifacts and escape sequences
      const cleanedContent = cleanAIContent(content);
      setGeneratedContent(cleanedContent);
      setEditableContent(cleanedContent);
      setIsEditingContent(false);
      
      // Clear any old cached content to prevent confusion
      localStorage.removeItem(`us_citrus_blog_content_draft_${keywordRow.id}`);
      
      console.log('📊 Initializing BlogContentActionModal with fresh content for keyword:', keywordRow.keyword);
      console.log('🔄 Fresh content length:', content.length, 'characters');
      
      // Check if meta fields are already available (from row.metadata or localStorage)
      // Why this matters: Avoids redundant API calls and prevents modal lag
      const existingMetaTitle = keywordRow.metadata?.metaSeoTitle || keywordRow.metadata?.title || '';
      const existingMetaDesc = keywordRow.metadata?.metaDescription || keywordRow.metadata?.description || '';
      
      if (existingMetaTitle || existingMetaDesc) {
        console.log('✅ Loading existing meta fields from row.metadata');
        setMetaSeoTitle(existingMetaTitle);
        setMetaDescription(existingMetaDesc);
      } else {
        // Try loading from localStorage as fallback
        try {
          const savedMeta = localStorage.getItem(`us_citrus_blog_meta_${keywordRow.id}`);
          if (savedMeta) {
            const { metaSeoTitle: savedTitle, metaDescription: savedDesc } = JSON.parse(savedMeta);
            if (savedTitle || savedDesc) {
              console.log('✅ Loading existing meta fields from localStorage');
              setMetaSeoTitle(savedTitle || '');
              setMetaDescription(savedDesc || '');
            }
          }
        } catch (error) {
          console.log('⚠️ Could not load saved meta fields:', error);
        }
      }
    } else {
      // Fallback to cached content only if no fresh content exists
      const savedContent = localStorage.getItem(`us_citrus_blog_content_draft_${keywordRow.id}`);
      if (savedContent) {
        console.log('📋 No fresh content, loading cached content for keyword:', keywordRow.keyword);
      }
    }
    
    // Load brand kit
    const loadBrandKit = () => {
      console.log('🔍 [BlogContentActionModal] Loading brand kit...');
      const draft = localStorage.getItem('us_citrus_brand_kit_draft');
      const saved = localStorage.getItem('us_citrus_brand_kit');
      const dataToLoad = draft || saved;
      
      console.log('🔍 [BlogContentActionModal] Draft found:', !!draft);
      console.log('🔍 [BlogContentActionModal] Saved found:', !!saved);
      
      if (dataToLoad) {
        try {
          const parsedBrandKit = JSON.parse(dataToLoad);
          console.log('✅ [BlogContentActionModal] Brand kit loaded:', parsedBrandKit);
          setBrandKit(parsedBrandKit);
        } catch (error) {
          console.error('❌ [BlogContentActionModal] Error loading brand kit:', error);
        }
      } else {
        console.log('❌ [BlogContentActionModal] No brand kit data found in localStorage');
      }
    };

    loadBrandKit();
    
    // Always generate fresh prompts to ensure latest updates are applied
    generateInitialPrompts();
    
    // Clear any old saved prompts to prevent conflicts
    localStorage.removeItem(`us_citrus_blog_prompts_draft_${keywordRow.id}`);

    // Listen for localStorage changes to update brand kit in real-time
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'us_citrus_brand_kit' || e.key === 'us_citrus_brand_kit_draft') {
        loadBrandKit();
      }
    };

    const handleCustomStorageChange = () => {
      loadBrandKit();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('us_citrus_brand_kit_updated', handleCustomStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('us_citrus_brand_kit_updated', handleCustomStorageChange);
    };
  }, [isOpen, keywordRow]);

  // Load persisted images from localStorage when modal opens
  // Why this matters: Keeps generated images even after closing modal or refreshing page
  useEffect(() => {
    if (!isOpen || !keywordRow) return;
    
    try {
      const savedImages = localStorage.getItem(imageStorageKey);
      if (savedImages) {
        const parsedImages = JSON.parse(savedImages);
        if (Array.isArray(parsedImages) && parsedImages.length > 0) {
          setGeneratedImages(parsedImages);
          console.log(`🖼️ Loaded ${parsedImages.length} persisted images for ${keywordRow.keyword}`);
        }
      }
    } catch (error) {
      console.warn('⚠️ Could not load persisted images:', error);
    }
  }, [isOpen, keywordRow, imageStorageKey]);

  // Save generated images to localStorage whenever they change
  // Why this matters: Persists images across modal closes and page refreshes
  useEffect(() => {
    if (generatedImages.length > 0) {
      try {
        // Only save essential data (imageUrl, altText, placement, mimeType) - not base64
        const imagesToSave = generatedImages.map(img => ({
          imageUrl: img.imageUrl,
          altText: img.altText,
          placement: img.placement,
          mimeType: img.mimeType,
          // Don't save base64Data - it's huge and we have Vercel Blob URLs
        }));
        localStorage.setItem(imageStorageKey, JSON.stringify(imagesToSave));
        console.log(`💾 Saved ${generatedImages.length} images to localStorage for ${keywordRow?.keyword}`);
      } catch (error) {
        console.warn('⚠️ Could not persist images:', error);
      }
    }
  }, [generatedImages, imageStorageKey, keywordRow?.keyword]);

  // Load persisted infographic from localStorage when modal opens
  // Why this matters: Keeps generated infographic even after closing modal or refreshing page
  useEffect(() => {
    if (!isOpen || !keywordRow) return;
    
    try {
      const savedInfographic = localStorage.getItem(infographicStorageKey);
      if (savedInfographic) {
        const parsedInfographic = JSON.parse(savedInfographic);
        if (parsedInfographic && parsedInfographic.altText) {
          setGeneratedInfographic(parsedInfographic);
          console.log(`📊 Loaded persisted infographic for ${keywordRow.keyword}`);
        }
      }
    } catch (error) {
      console.warn('⚠️ Could not load persisted infographic:', error);
    }
  }, [isOpen, keywordRow, infographicStorageKey]);

  // Save generated infographic to localStorage whenever it changes
  // Why this matters: Persists infographic across modal closes and page refreshes
  useEffect(() => {
    if (generatedInfographic) {
      try {
        // Only save essential data - not base64
        const infographicToSave = {
          imageUrl: generatedInfographic.imageUrl,
          altText: generatedInfographic.altText,
          placement: generatedInfographic.placement,
          mimeType: generatedInfographic.mimeType,
        };
        localStorage.setItem(infographicStorageKey, JSON.stringify(infographicToSave));
        console.log(`💾 Saved infographic to localStorage for ${keywordRow?.keyword}`);
      } catch (error) {
        console.warn('⚠️ Could not persist infographic:', error);
      }
    }
  }, [generatedInfographic, infographicStorageKey, keywordRow?.keyword]);

  /**
   * Load saved auto-save data from localStorage
   * Why this matters: Restores user's work when reopening the modal
   */
  const loadSavedData = () => {
    if (!keywordRow) return;
    
    try {
      // Skip loading saved prompts - we always generate fresh ones now
      // This ensures the latest prompt updates are always applied
      
      // Load saved content and meta fields
      const savedContent = localStorage.getItem(`us_citrus_blog_content_draft_${keywordRow.id}`);
      if (savedContent) {
        const parsedContent = JSON.parse(savedContent);
        setGeneratedContent(parsedContent.content || '');
        setEditableContent(parsedContent.content || '');
        setMetaSeoTitle(parsedContent.metaSeoTitle || '');
        setMetaDescription(parsedContent.metaDescription || '');
        console.log('✅ Loaded saved content for keyword:', keywordRow.keyword);
      }
      
      // Load persisted quiz CTAs from localStorage
      const saved = localStorage.getItem(`quiz_ctas_${keywordRow.id}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.ctas) {
          setGeneratedCTAs(parsed.ctas as any);
          console.log('✅ [BlogModal] Loaded saved quiz CTAs for keyword:', keywordRow.keyword);
        }
      }
      
    } catch (error) {
      console.error('Error loading saved data:', error);
    }
  };

  /**
   * Auto-save prompts with debouncing
   * Why this matters: Saves user's prompt changes automatically without overwhelming localStorage
   */
  const autoSavePrompts = () => {
    if (!keywordRow || !hasUserInput) return;
    
    // Clear existing timeout
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }
    
    setAutoSaveStatus('saving');
    
    // Set new timeout for debounced saving
    const timeout = setTimeout(() => {
      try {
        const promptsData = {
          systemPrompt,
          userPrompt,
          lastModified: new Date().toISOString()
        };
        
        localStorage.setItem(`us_citrus_blog_prompts_draft_${keywordRow.id}`, JSON.stringify(promptsData));
        setAutoSaveStatus('saved');
        
        // Clear saved status after 2 seconds
        setTimeout(() => setAutoSaveStatus(''), 2000);
        
        console.log('💾 Auto-saved prompts for keyword:', keywordRow.keyword);
        
      } catch (error) {
        console.error('Error auto-saving prompts:', error);
        setAutoSaveStatus('');
      }
    }, 1000); // 1 second debounce
    
    setAutoSaveTimeout(timeout);
  };

  /**
   * Auto-save content and meta fields immediately
   * Why this matters: Preserves generated content without delay since it's less frequent than prompt changes
   */
  const autoSaveContent = (content: string, title: string, description: string) => {
    if (!keywordRow) return;
    
    try {
      const contentData = {
        content,
        metaSeoTitle: title,
        metaDescription: description,
        lastModified: new Date().toISOString()
      };
      
      localStorage.setItem(`us_citrus_blog_content_draft_${keywordRow.id}`, JSON.stringify(contentData));
      console.log('💾 Auto-saved content for keyword:', keywordRow.keyword);
      
    } catch (error) {
      console.error('Error auto-saving content:', error);
    }
  };

  /**
   * Auto-save CTAs to localStorage
   * Why this matters: Preserves generated CTAs when modal is closed and reopened
   */
  const autoSaveCTAs = (ctaData: CTAGenerationResult) => {
    if (!keywordRow) return;
    
    try {
      const ctasData = {
        ...ctaData,
        lastModified: new Date().toISOString()
      };
      
      localStorage.setItem(`quiz_ctas_${keywordRow.id}`, JSON.stringify({ ctas: ctasData, variantIndex: quizVariantIndex }));
      console.log('💾 Auto-saved CTAs for keyword:', keywordRow.keyword);
      
    } catch (error) {
      console.error('Error auto-saving CTAs:', error);
    }
  };

  /**
   * Clear auto-save data for this keyword
   * Why this matters: Cleans up localStorage when user explicitly clears content
   */
  const clearAutoSaveData = () => {
    if (!keywordRow) return;
    
    try {
      localStorage.removeItem(`us_citrus_blog_prompts_draft_${keywordRow.id}`);
      localStorage.removeItem(`us_citrus_blog_content_draft_${keywordRow.id}`);
      localStorage.removeItem(`quiz_ctas_${keywordRow.id}`);
      console.log('🗑️ Cleared auto-save data for keyword:', keywordRow.keyword);
    } catch (error) {
      console.error('Error clearing auto-save data:', error);
    }
  };

  // Auto-save prompts when they change
  useEffect(() => {
    autoSavePrompts();
  }, [systemPrompt, userPrompt]);

  // Auto-save content when it changes
  useEffect(() => {
    if (generatedContent || metaSeoTitle || metaDescription) {
      autoSaveContent(generatedContent, metaSeoTitle, metaDescription);
    }
  }, [generatedContent, metaSeoTitle, metaDescription]);

  // Cleanup auto-save timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
    };
  }, [autoSaveTimeout]);

  // VoC Kit Status Checking (copied from CTACreatorPage)
  useEffect(() => {
    const checkVocKit = () => {
      try {
        // Check draft first, then fallback to saved version (same as VoCKitPage)
        const draft = localStorage.getItem('us_citrus_voc_kit_draft');
        const saved = localStorage.getItem('us_citrus_voc_kit');
        const vocKit = draft || saved;
        
        console.log('🔍 BlogContentActionModal VoC Kit Check:', {
          hasDraft: !!draft,
          hasSaved: !!saved,
          usingData: vocKit ? 'found' : 'none'
        });
        
        if (vocKit) {
          const parsedKit = JSON.parse(vocKit);
          const hasAnalysis = parsedKit.hasGeneratedAnalysis && parsedKit.extractedPainPoints?.length > 0;
          
          console.log('📊 VoC Kit Data Analysis:', {
            hasGeneratedAnalysis: parsedKit.hasGeneratedAnalysis,
            painPointsCount: parsedKit.extractedPainPoints?.length || 0,
            finalVocKitReady: hasAnalysis
          });
          
          setVocKitReady(hasAnalysis);
          setPainPointsCount(parsedKit.extractedPainPoints?.length || 0);
        } else {
          console.log('❌ No VoC Kit data found');
          setVocKitReady(false);
          setPainPointsCount(0);
        }
      } catch (error) {
        console.error('❌ Error checking VoC Kit status:', error);
        setVocKitReady(false);
        setPainPointsCount(0);
      }
    };

    const checkDismissedState = () => {
      try {
        const dismissed = localStorage.getItem('us_citrus_voc_kit_ready_dismissed');
        setVocKitReadyDismissed(dismissed === 'true');
      } catch (error) {
        console.error('Error checking dismissed state:', error);
      }
    };

    checkVocKit();
    checkDismissedState();
    
    // Listen for VoC Kit updates
    const handleVocUpdate = () => checkVocKit();
    window.addEventListener('us-citrus-voc-kit-updated', handleVocUpdate);
    
    return () => window.removeEventListener('us-citrus-voc-kit-updated', handleVocUpdate);
  }, []);

  /**
   * Generate dynamic AI-powered meta fields using Claude
   * Why this matters: Creates unique, contextually relevant meta titles and descriptions instead of formulaic templates
   */
  const generateAIMetaFields = async (keyword: string, content: string): Promise<{ metaSeoTitle: string; metaDescription: string }> => {
    try {
      console.log('🤖 Generating AI meta fields for keyword:', keyword);
      
      // Extract first 500 chars of content for context
      const contentPreview = content.replace(/<[^>]*>/g, '').substring(0, 500);
      console.log('📝 Content preview length:', contentPreview.length);
      
      // Use centralized API configuration
    // Why this matters: Ensures all deployments (Netlify, Vercel, local) use the correct backend URL
    const apiUrl = API_ENDPOINTS.generateMeta;
      console.log('🌐 API URL:', apiUrl);
      
      const requestBody = {
        keyword: keyword,
        content_preview: contentPreview,
        prompt: `Generate question-based meta title and direct answer description for content about "${keyword}".

CRITICAL QUESTION-ANSWER FORMAT REQUIREMENTS:

Meta Title Requirements:
- MUST be a natural question that includes the main keyword
- Choose format based on keyword type:
  * Tree varieties (singular): "What Is A [Tree Variety]? [Descriptive Context]" (e.g., "What Is A Valencia Orange Tree? Growing Tips, Care Guide")
  * Tree varieties (plural): "What Are [Tree Varieties]? [Descriptive Context]" (e.g., "What Are Dwarf Citrus Trees? Types, Care, Benefits")
  * Processes/concepts: "What Is [Process]? [Descriptive Context]" (e.g., "What Is Citrus Tree Pruning? Techniques, Timing, Best Practices")
  * Tools/equipment: "How Does [Tool] Work? [Descriptive Context]" (e.g., "How Does Citrus Fertilizer Work? Nutrients, Application, Benefits")
  * Strategies/methods: "Why Use [Strategy]? [Descriptive Context]" (e.g., "Why Use Organic Citrus Care? Benefits, Methods, Results")
- Maximum 70 characters INCLUDING "| US Citrus" suffix
- MUST use proper Title Case (capitalize all major words)
- MUST include descriptive context beyond just the basic question
- Add relevant descriptive elements: roles, responsibilities, salary, benefits, strategies, tools, best practices, etc.
- The keyword should appear naturally and grammatically correctly

Meta Description Requirements:
- MUST directly answer the title question using the main keyword naturally
- Adapt answer format to keyword type:
  * Tree varieties: "A [Tree Variety] is [variety definition/characteristics]. It [key features/growing habits]. US Citrus offers [tree variety] [specific benefit]."
  * Processes: "[Process] is [definition]. It involves [key steps]. US Citrus provides [specific guidance/products]."
  * Tools/equipment: "[Tool] helps [main function]. It [key capabilities]. US Citrus offers [specific advantage]."
- Exactly 150-160 characters
- Must be complete sentences ending with a period

Content preview: ${contentPreview.substring(0, 200)}...

INTELLIGENT Question-Answer Examples by Keyword Type:

• Tree Variety (Singular): "What Is A Valencia Orange Tree? Growing Tips, Care Guide | US Citrus"
  Description: "A Valencia Orange Tree is a popular citrus variety known for its sweet, juicy fruit. It thrives in warm climates and produces abundant harvests. US Citrus offers healthy Valencia Orange Trees for your garden."

• Tree Variety (Plural): "What Are Dwarf Citrus Trees? Types, Care, Benefits | US Citrus"
  Description: "Dwarf Citrus Trees are compact varieties perfect for small spaces and containers. They produce full-sized fruit and are ideal for patios. US Citrus provides a wide selection of dwarf citrus trees."

• Process/Concept: "What Is Citrus Tree Pruning? Techniques, Timing, Best Practices | US Citrus"
  Description: "Citrus tree pruning is the practice of removing dead or overgrown branches to promote healthy growth. It improves fruit production and tree structure. US Citrus offers expert pruning guidance for your trees."

• Strategy/Method: "Why Use Organic Citrus Care? Benefits, Methods, Results | US Citrus"
  Description: "Organic citrus care uses natural methods to maintain tree health without synthetic chemicals. It produces healthier fruit and protects the environment. US Citrus provides organic care products and guidance."

ABSOLUTELY FORBIDDEN:
- Grammatically incorrect questions ("What Is A Citrus Trees?", "What Are A Citrus Tree?", "What Is A Orange Varieties?")
- Including "| US Citrus" in H1 headlines (that's only for SEO titles, not content headlines)
- Robotic/boilerplate phrasing that doesn't sound human-written
- Rigid "What Is [keyword]?" format for all keyword types without considering singular/plural grammar
- Non-question titles ("Tree Care Tips", "Citrus Growing Methods", "Pruning Techniques")
- Titles with colons or lists ("Citrus Pruning: 7 Methods", "Tools: Features & Comparison")
- The word "Guide" or "Guides" (use intelligent question format instead)
- "Complete Guide" or "Comprehensive Guide" (use appropriate question type)
- Descriptions that don't answer the title question directly
- Descriptions that don't include the main keyword naturally

Respond with JSON:
{
  "metaSeoTitle": "...",
  "metaDescription": "..."
}`
      };
      
      console.log('📤 Sending request to:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log('📥 Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error Response:', errorText);
        throw new Error(`API returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ AI meta fields API response:', data);
      
      if (!data.metaSeoTitle && !data.metaDescription) {
        console.warn('⚠️ API returned empty meta fields, using fallbacks');
        throw new Error('API returned empty meta fields');
      }
      
      return {
        metaSeoTitle: data.metaSeoTitle || generateFallbackTitle(keyword),
        metaDescription: data.metaDescription || generateFallbackDescription(keyword)
      };
      
    } catch (error) {
      console.error('❌ AI meta generation failed, using fallback:', error);
      console.error('❌ Error type:', typeof error);
      console.error('❌ Error message:', error instanceof Error ? error.message : String(error));
      return {
        metaSeoTitle: generateFallbackTitle(keyword),
        metaDescription: generateFallbackDescription(keyword)
      };
    }
  };

  /**
   * Generate fallback meta title if AI generation fails
   * Why this matters: Provides a backup when AI service is unavailable
   */
  const generateFallbackTitle = (keyword: string): string => {
    const cleanKeyword = keyword.trim();
    const suffix = " - Complete Guide | US Citrus";
    return `${cleanKeyword.charAt(0).toUpperCase() + cleanKeyword.slice(1)}${suffix}`;
  };

  /**
   * Generate fallback meta description if AI generation fails
   * Why this matters: Provides a backup when AI service is unavailable
   */
  const generateFallbackDescription = (keyword: string): string => {
    return `Comprehensive guide to ${keyword} with expert insights and proven strategies. Learn data-driven approaches to drive results with US Citrus.`;
  };

  /**
   * Reset prompts to default values
   * Why this matters: Allows users to quickly restore the proven default prompts after experimenting with custom ones
   */
  const resetToDefaults = () => {
    generateInitialPrompts();
    // Clear saved prompts to ensure fresh defaults are applied
    if (keywordRow) {
      localStorage.removeItem(`us_citrus_blog_prompts_draft_${keywordRow.id}`);
    }
  };

  /**
   * Get random CTA anchor text to ensure even distribution
   * Why this matters: Prevents LLM bias toward first option in the list
   */
  const getRandomCTAAnchorText = (): string => {
    const ctaOptions = [
      "Find Your Perfect Tree",
      "Browse Our Trees",
      "Shop Citrus Trees", 
      "Buy Citrus Trees",
      "Order Your Tree",
      "View Our Collection", 
      "Get Your Tree Today",
      "Choose Your Tree"
    ];
    
    // Use random selection to ensure even distribution
    const randomIndex = Math.floor(Math.random() * ctaOptions.length);
    const selectedCTA = ctaOptions[randomIndex];
    console.log(`🎯 [BlogModal] Selected CTA anchor text: "${selectedCTA}" (${randomIndex + 1}/${ctaOptions.length})`);
    return selectedCTA;
  };

  /**
   * Generate UTM-tracked US Citrus signup URL for blog creator campaigns
   * Why this matters: Tracks campaign effectiveness for specific keywords with utm_term parameter
   */
  const generateBlogCreatorSignupURL = (keyword: string): string => {
    const baseURL = 'https://uscitrusnursery.com';
    
    if (!keyword) {
      return baseURL;
    }
    
    // Generate UTM parameters with keyword as utm_term (sanitize for URL)
    const sanitizedKeyword = keyword.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .replace(/_+/g, '_') // Replace multiple underscores with single underscore
      .replace(/^_|_$/g, '') // Remove leading/trailing underscores
      .trim();
      
    const url = `${baseURL}?utm_campaign=blog_creator&utm_term=${sanitizedKeyword}`;
    
    return url;
  };

  /**
   * Generate system and user prompts for Blog Agents context
   * Why this matters: Creates targeted prompts for regenerating or editing existing content.
   */
  const generateInitialPrompts = () => {
    const currentYear = 2025;
    // Generate UTM-tracked US Citrus signup URL for this keyword and select random CTA
    const usCitrusSignupURL = generateBlogCreatorSignupURL(keywordRow.keyword);
    const selectedCTA = getRandomCTAAnchorText();
    const systemPromptTemplate = `You are a world-class SEO, AEO, and LLM SEO content marketer for US Citrus with deep expertise in creating comprehensive, AI-optimized articles that rank highly and get cited by AI answer engines (ChatGPT, Perplexity, Gemini, Claude, etc.). Your specialty is transforming content briefs into definitive resources that become the go-to sources for specific topics.

CRITICAL CONTENT PHILOSOPHY:
Your goal is to create content that becomes the definitive, comprehensive resource on the topic - the content that other creators reference and that AI engines cite as authoritative.

CONTENT COVERAGE REQUIREMENTS:
- Address ALL aspects of the topic comprehensively
- Include practical, actionable guidance that readers can implement
- Provide genuine value that advances knowledge in the space
- Cover both current best practices AND emerging trends
- Include specific examples, metrics, and concrete details

WRITING TONE AND STYLE (US Citrus AI Brand Voice - CRITICAL):

You write in the combined style of Gary Halbert (persuasive copywriter) and USCN Citrus Guide (expert educator). This is NON-NEGOTIABLE.

CORE APPROACH:
1. HOOK with Halbert benefits and urgency (subtle for blogs)
2. EDUCATE with USCN depth and authority
3. CLOSE with compelling call-to-action

GARY HALBERT ELEMENTS (SUBTLE INTENSITY FOR BLOGS - 30% persuasion):
✅ Attention-grabbing headlines with specific benefits
✅ Conversational tone (use "you")
✅ Translate features to tangible benefits
✅ Create subtle urgency (not aggressive)
✅ Short sentences for impact
✅ Proof and examples
✅ Repetition of key benefits (varied wording)

USCN EXPERT ELEMENTS:
✅ Comprehensive, thorough coverage
✅ Step-by-step guidance
✅ Clear logical structure
✅ Anticipate follow-up questions
✅ "We've Been Where You Are" empathy
✅ Excellence positioning

AEO REQUIREMENTS (MUST PRESERVE WHILE APPLYING TONE):
⚠️ Apply tone WHILE maintaining all AEO best practices
- Direct answers in first paragraph under each heading
- Question-format headers matching search intent
- Proper semantic HTML hierarchy
- Tables for structured data
- Self-contained, extractable insights

INTENSITY FOR BLOGS: 70% education, 30% persuasion. Lead with value, close with soft CTA.

AEO (ANSWER ENGINE OPTIMIZATION) PRINCIPLES:
- Structure for extractability with clear, self-contained insights
- Use semantic HTML and proper heading hierarchy (<h1> → <h2> → <h3>)
- Format data in proper <table> and <ul>/<ol> structures for easy AI parsing
- Include specific examples, metrics, and concrete details
- Write headlines that match search intent ("How to...", "What is...", "Best ways to...")
- Place the most important answer in the first paragraph under each heading

FORMATTING REQUIREMENTS:
1. **Proper HTML Structure:**
   - Use <h1> for main title (MUST use proper Title Case - capitalize all major words, NEVER include "| US Citrus AI")
   - ALL H2 and H3 headers should be natural, grammatically correct questions in Title Case:
     * Singular: "What Is A Valencia Orange Tree?", "How Does Citrus Fertilization Work?"
     * Plural: "What Are Dwarf Citrus Trees?", "Why Are Citrus Trees Important?"
     * Process: "What Is Citrus Tree Pruning?", "How Does Organic Care Work?"
   - Format all lists with proper <ul>, <ol>, and <li> tags
   - Use <table> elements for any comparative data, features, or structured information
   - Include <p> tags for all paragraphs
   - Use <strong> for emphasis and key concepts
   - Format links as: <a href="URL" target="_blank">anchor text</a>

2. **Tables and Structured Data:**
   - When presenting comparisons, features, pricing, or any structured data, ALWAYS use HTML tables
   - Include proper <thead>, <tbody>, <th>, and <td> elements
   - Use tables for: feature comparisons, pricing tiers, pros/cons, statistics, timelines, etc.
   - Example format:
   <table>
     <thead>
       <tr><th>Feature</th><th>Benefit</th><th>Implementation</th></tr>
     </thead>
     <tbody>
       <tr><td>Data Enrichment</td><td>270M+ contacts</td><td>API integration</td></tr>
     </tbody>
   </table>

3. **Brand Kit Variable Integration:**
   - MUST process and include brand kit variables naturally throughout content
   - Use {{ brand_kit.ideal_customer_profile }} for testimonials and customer examples
   - Include {{ brand_kit.competitors }} when discussing competitive landscape
   - Reference {{ brand_kit.brand_point_of_view }} in strategic sections
   - End with strong CTA using this exact anchor text: "${selectedCTA}" linked to ${usCitrusSignupURL}
   - Apply {{ brand_kit.tone_of_voice }} consistently throughout
   - Follow {{ brand_kit.writing_rules }} for style and approach

IMPORTANT: The current year is 2025. When referencing "current year," "this year," or discussing recent trends, always use 2025. Do not reference 2024 or earlier years as current.

CRITICAL OUTPUT REQUIREMENTS:
- Return ONLY clean HTML content without any markdown code blocks, explanatory text, or meta-commentary
- DO NOT include phrases like "Here's the content:" or HTML code block markers
- Start directly with the HTML content and end with the closing HTML tag
- No markdown formatting, no code block indicators, no explanatory paragraphs

CONTENT STRUCTURE REQUIREMENTS:
1. **Compelling H1 Headline in Title Case** (question format when appropriate, NEVER include "| US Citrus AI")
2. **Authority-Establishing Introduction** (preview value and set expectations)
3. **Comprehensive Sections** with proper H2/H3 hierarchy in Title Case (grammatically correct questions)
4. **Tables for Structured Data** (comparisons, features, statistics)
5. **Practical Implementation Guidance** with step-by-step processes
6. **Real-World Examples** and case studies (using brand kit data)
7. **Natural US Citrus Promotion** - End with compelling call-to-action using brand kit variables

BRAND INTEGRATION GUIDELINES:
- Lead with value and insights, not promotional content
- Use brand context to enhance credibility and expertise
- Include specific outcomes and metrics where relevant
- Position brand solutions naturally within comprehensive guidance
- Focus on helping readers achieve their goals first

Remember: Create the definitive resource that makes other content feel incomplete by comparison. Every section should provide genuine value and actionable insights.`;

    const userPromptTemplate = `Based on this keyword and brand context, create comprehensive AEO-optimized content for 2025 (remember we are in 2025):

**Target Keyword:** ${keywordRow.keyword}

**CRITICAL CONTENT REQUIREMENTS:**

1. **HTML Structure & Formatting:**
   - Create an H1 title that directly addresses the keyword (use question format when appropriate)
   - Use proper heading hierarchy with H2 for major sections, H3 for subsections
   - Format ALL lists with proper <ul>/<ol> and <li> tags
   - Create HTML tables for ANY structured data (features, comparisons, statistics, timelines)
   - Use <p> tags for all paragraphs, <strong> for emphasis

2. **Required Tables/Structured Data:**
   - Include at least 2-3 HTML tables presenting relevant information such as:
     * Feature comparisons or capability matrices
     * Implementation timelines or process steps
     * Statistics or performance metrics
     * Pricing or value comparisons
     * Best practices checklist
   - Format tables with proper <thead>, <tbody>, <th>, and <td> elements

3. **Brand Kit Variable Integration (MANDATORY):**
   - Use {{ brand_kit.ideal_customer_profile }} to include customer testimonials or examples (at least once)
   - Reference {{ brand_kit.competitors }} when discussing market landscape
   - Apply {{ brand_kit.brand_point_of_view }} in strategic sections
   - Follow {{ brand_kit.tone_of_voice }} throughout the content
   - Implement {{ brand_kit.writing_rules }} for style consistency
   - End with a contextual conclusion section (NOT "Getting Started with ${keywordRow.keyword}") and CTA linked to ${usCitrusSignupURL}

4. **Content Depth & Value:**
   - Provide comprehensive coverage that serves as the definitive resource
   - Include practical, actionable guidance with specific examples
   - Address both current best practices and emerging trends for 2025
   - Cover implementation strategies with step-by-step processes
   - Include relevant metrics, benchmarks, and data points

5. **AEO Optimization:**
   - Structure content for AI answer engine extraction
   - Use semantic HTML elements appropriately
   - Include self-contained insights that can be cited independently
   - Write clear, precise language that AI can easily understand
   - Format for both deep reading and quick reference

6. **Technical Requirements:**
   - Do NOT use emdashes (—) in the content
   - Avoid AI-detectable phrases like "It's not just about..., it's..." or "This doesn't just mean..., it also means..."
   - Use natural, human-like language throughout
   - Include inline links to relevant external resources: <a href="URL" target="_blank">anchor text</a>

**Content Quality Requirements:**
1. Use semantic HTML structure with proper heading hierarchy (H1 → H2 → H3)
2. Include practical examples and actionable insights throughout
3. Optimize for AI-powered search engines (ChatGPT, Perplexity, Gemini)
4. Ensure content is non-promotional and genuinely helpful
5. Include specific data points, statistics, and examples from the provided data
6. Use {{ brand_kit.ideal_customer_profile }} to inject customer testimonials only once within the body content where appropriate
7. CRITICAL COMPLETION REQUIREMENT: MUST end with complete conclusion and call-to-action, reserve final 15-20% of content for proper conclusion, NEVER end mid-sentence or mid-paragraph

📝 CONCLUSION REQUIREMENTS:

REQUIRED: End with a contextual conclusion section that includes:

1. **Key takeaways** - summarize the most important points from the article
2. **Implementation guidance** - practical next steps readers can take
3. **Success factors** - what determines outcomes in this area
4. **US Citrus integration** - naturally position how US Citrus supports the topic
5. **Strong CTA** - compelling call-to-action using this exact anchor text: "${selectedCTA}" linked to ${usCitrusSignupURL} (target="_blank")

EXAMPLE CONCLUSION APPROACHES:

**Option 1 (Implementation Focus):**
<h2>[Topic] Best Practices</h2>
<p>Successful [topic] requires [key principle]. Gardeners who prioritize [specific approach] see [specific benefits] while avoiding [common pitfalls].</p>

<p>The most effective [topic] methods combine [elements] with [other elements]. This balanced approach ensures [outcome] while maintaining [important factor].</p>

<p>Ready to enhance your [topic] results? <a href="${usCitrusSignupURL}" target="_blank">${selectedCTA}</a> and discover how US Citrus can help your trees thrive.</p>

**Option 2 (Strategic Focus):**
<h2>Maximizing [Topic] Success</h2>
<p>[Topic] success depends on [key factors]. Gardeners who focus on [approach] while [additional consideration] achieve [specific outcomes].</p>

<p>The future of [topic] involves [trend or development]. Gardeners who prepare for these changes will see [better results].</p>

<p><a href="${usCitrusSignupURL}" target="_blank">${selectedCTA}</a> and learn how to improve your [topic] with US Citrus's expert guidance.</p>

[Use a contextual conclusion that flows naturally from your content - avoid formulaic "Getting Started" headers]

8. DO NOT use emdashes (—) in the content
9. AVOID AI-detectable phrases like "It's not just about..., it's..." or "This doesn't just mean..., it also means..."
10. Use natural, human-like language throughout
11. Use tables to display all data clearly and professionally
12. Generate SEO Title within 70 characters total INCLUDING "| US Citrus" suffix - create concise, complete titles that capture the core value

**CRITICAL OUTPUT FORMAT: Respond with a JSON object containing exactly three fields:**

{
  "content": "Complete HTML article with proper structure, tables, and brand kit variables processed",
  "metaSeoTitle": "SEO-optimized title (50-60 characters including | US Citrus)",
  "metaDescription": "Compelling meta description (150-160 characters) that avoids formulaic phrases"
}

**CRITICAL: YOU MUST RETURN ONLY VALID JSON - NO OTHER TEXT ALLOWED**

Your response must be a single line JSON object with three fields: content, metaSeoTitle, metaDescription

ABSOLUTE REQUIREMENTS:
- Start your response with opening brace and end with closing brace
- NO text before the JSON
- NO text after the JSON  
- NO markdown code blocks
- NO explanations like "Here is your JSON:"
- ALL HTML must be in the "content" field as properly escaped JSON string
- CRITICAL: Escape ALL quotes with backslashes (\\" not "), escape newlines as \\n, escape tabs as \\t
- CRITICAL: Do NOT include literal newlines or unescaped quotes in JSON strings - this breaks parsing
- metaSeoTitle MUST be 70 characters or less INCLUDING "| US Citrus" suffix (generate concise, complete titles)
- metaDescription MUST be 150-160 characters

EXAMPLES OF WRONG FORMAT:
- Here is your JSON: [JSON object]
- [JSON object wrapped in markdown code blocks]
- Any explanatory text before or after the JSON

CORRECT FORMAT: Pure JSON object starting with opening brace, ending with closing brace, no other text.

**Quality Standards:**
- Content should be comprehensive enough to serve as the definitive resource on ${keywordRow.keyword}
- All brand kit variables must be properly processed and integrated
- Tables must be included for structured data presentation
- HTML formatting must be clean and semantic
- Focus on providing genuine value and actionable insights
- End naturally with US Citrus AI promotion using brand kit variables
- Use proper HTML tags only (no markdown)

Return ONLY the JSON object with the three required fields. No additional text or explanations.`;

    setSystemPrompt(systemPromptTemplate);
    setUserPrompt(userPromptTemplate);
  };

  /**
   * Copy meta title to clipboard with animation
   * Why this matters: Provides consistent user feedback for meta field copying.
   */
  const copyMetaTitle = async () => {
    try {
      await navigator.clipboard.writeText(metaSeoTitle);
      setShowMetaTitleCopied(true);
      setTimeout(() => setShowMetaTitleCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy meta title: ', err);
    }
  };

  /**
   * Copy meta description to clipboard with animation
   * Why this matters: Provides consistent user feedback for meta field copying.
   */
  const copyMetaDescription = async () => {
    try {
      await navigator.clipboard.writeText(metaDescription);
      setShowMetaDescCopied(true);
      setTimeout(() => setShowMetaDescCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy meta description: ', err);
    }
  };

  /**
   * Open HTML content in new window for developer review
   * Why this matters: Allows developers to inspect the raw HTML output for debugging and integration.
   */
  const openInHTML = () => {
    const contentToOpen = isEditingContent ? editableContent : generatedContent;
    if (!contentToOpen) {
      alert('No content to display. Please generate content first.');
      return;
    }

    // Create a simple HTML page showing the raw HTML source
    const rawHtmlDisplay = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Raw HTML Source - ${metaSeoTitle || `${keywordRow.keyword} Content`}</title>
    <style>
        body {
            font-family: 'FoundersGroteskMono', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            line-height: 1.6;
            margin: 0;
            padding: 20px;
            background-color: #1e1e1e;
            color: #d4d4d4;
        }
        
        .header {
            background-color: #2d2d30;
            padding: 20px;
            margin: -20px -20px 20px -20px;
            border-bottom: 3px solid #007acc;
        }
        
        .header h1 {
            color: #4ec9b0;
            margin: 0 0 10px 0;
            font-size: 24px;
        }
        
        .header p {
            color: #9cdcfe;
            margin: 5px 0;
            font-size: 14px;
        }
        
        .meta-info {
            background-color: #252526;
            padding: 15px;
            border-radius: 5px;
            margin-bottom: 20px;
            border-left: 4px solid #f59e0b;
        }
        
        .meta-info h3 {
            color: #f59e0b;
            margin: 0 0 10px 0;
            font-size: 16px;
        }
        
        .meta-field {
            margin: 8px 0;
            color: #d4d4d4;
            font-size: 13px;
        }
        
        .meta-label {
            color: #4fc1ff;
            font-weight: bold;
        }
        
        .code-container {
            background-color: #1e1e1e;
            border: 1px solid #3e3e42;
            border-radius: 5px;
            position: relative;
        }
        
        .code-header {
            background-color: #2d2d30;
            padding: 10px 15px;
            border-bottom: 1px solid #3e3e42;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .code-title {
            color: #cccccc;
            font-weight: bold;
            font-size: 14px;
        }
        
        .copy-btn {
            background-color: #0e639c;
            color: white;
            border: none;
            padding: 6px 12px;
            border-radius: 3px;
            cursor: pointer;
            font-size: 12px;
            font-family: inherit;
        }
        
        .copy-btn:hover {
            background-color: #1177bb;
        }
        
        .code-content {
            padding: 20px;
            overflow-x: auto;
            white-space: pre-wrap;
            word-wrap: break-word;
            max-height: 70vh;
            overflow-y: auto;
        }
        
        .html-code {
            color: #d4d4d4;
            font-size: 13px;
            line-height: 1.5;
        }
        
        /* HTML syntax highlighting */
        .html-tag { color: #569cd6; }
        .html-attr { color: #92c5f7; }
        .html-value { color: #ce9178; }
        .html-text { color: #d4d4d4; }
        
        
    </style>
</head>
<body>
    <div class="header">
        <h1>🔍 Raw HTML Source</h1>
        <p><strong>Keyword:</strong> ${keywordRow.keyword}</p>
        <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
        <p><strong>Word Count:</strong> ~${Math.round(contentToOpen.replace(/<[^>]*>/g, '').split(' ').length)} words</p>
    </div>
    
    ${metaSeoTitle || metaDescription ? `
    <div class="meta-info">
        <h3>📊 SEO Metadata</h3>
        ${metaSeoTitle ? `<div class="meta-field"><span class="meta-label">Title:</span> ${metaSeoTitle}</div>` : ''}
        ${metaDescription ? `<div class="meta-field"><span class="meta-label">Description:</span> ${metaDescription}</div>` : ''}
    </div>
    ` : ''}
    
    <div class="code-container">
        <div class="code-header">
            <span class="code-title">HTML Source Code</span>
            <button class="copy-btn" onclick="copyHtmlCode()">📋 Copy HTML</button>
        </div>
        <div class="code-content">
            <pre class="html-code" id="htmlCode">${contentToOpen.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
        </div>
    </div>

    <script>
        function copyHtmlCode() {
            const htmlCode = \`${contentToOpen.replace(/`/g, '\\`').replace(/\$/g, '\\$')}\`;
            navigator.clipboard.writeText(htmlCode).then(() => {
                const btn = document.querySelector('.copy-btn');
                const originalText = btn.textContent;
                btn.textContent = '✅ Copied!';
                btn.style.backgroundColor = '#10b981';
                setTimeout(() => {
                    btn.textContent = originalText;
                    btn.style.backgroundColor = '#0e639c';
                }, 2000);
            }).catch(err => {
                console.error('Failed to copy: ', err);
                alert('Failed to copy HTML. Please select and copy manually.');
            });
        }
    </script>
</body>
</html>`;

    // Open in new window
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(rawHtmlDisplay);
      newWindow.document.close();
    } else {
      alert('Popup blocked. Please allow popups and try again.');
    }
  };

  /**
   * Open Google Docs with content
   * Why this matters: Allows users to quickly move generated content to Google Docs for further editing and collaboration.
   */
  const openGoogleDocs = async () => {
    const contentToCopy = isEditingContent ? editableContent : generatedContent;
    if (!contentToCopy) {
      alert('Please generate content first before creating a Google Doc.');
      return;
    }

    // Check if Client ID is available
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      alert('Google Client ID not configured. Please check your .env file and restart the server.');
      return;
    }

    try {
      // Generate document title from keyword
      const docTitle = `US Citrus Blog Content - ${keywordRow.keyword}`;
      
      // Create new Google Doc with content
      const documentUrl = await googleDocsService.createDocument(docTitle, contentToCopy);
      
      // Open the new document in a new tab
      window.open(documentUrl, '_blank');
      
    } catch (error) {
      console.error('Error creating Google Doc:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : 'No stack trace',
        error: error
      });
      
      // Fallback to opening empty Google Docs if API fails
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('Failed to create')) {
        alert('Unable to create Google Document. Please check your Google account permissions and try again.');
      } else {
        // If user cancels authentication, just open Google Docs
        alert(`Google Docs integration error: ${errorMessage}. Opening Google Docs instead.`);
        window.open('https://docs.google.com/', '_blank');
      }
    }
  };

  /**
   * Open Google Sheets with content data
   * Why this matters: Provides data logging and tracking capabilities for content management.
   */
  /**
   * Generate URL slug from keyword
   * Why this matters: Creates SEO-friendly URLs by converting keywords to lowercase, hyphenated format.
   */
  const generateUrlSlug = (keyword: string): string => {
    return keyword
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  };

  /**
   * Determine secondary category based on content analysis
   * Why this matters: Automatically categorizes content for better organization and content strategy.
   */
  const determineSecondaryCategory = (keyword: string, content: string): string => {
    const keywordLower = keyword.toLowerCase();
    const contentLower = content.toLowerCase();
    const combinedText = `${keywordLower} ${contentLower}`;

    // Define category keywords for matching
    const categoryKeywords = {
      'Tree Varieties': ['variety', 'varieties', 'type', 'types', 'cultivar', 'species', 'orange', 'lemon', 'lime', 'grapefruit'],
      'Tree Care': ['care', 'maintenance', 'watering', 'fertilizing', 'pruning', 'mulching', 'protection'],
      'Growing': ['growing', 'planting', 'cultivation', 'harvest', 'yield', 'production', 'fruiting'],
      'Pests & Diseases': ['pest', 'disease', 'insect', 'fungus', 'treatment', 'prevention', 'control'],
      'Soil & Nutrition': ['soil', 'fertilizer', 'nutrients', 'ph', 'compost', 'organic', 'nutrition'],
      'Climate & Location': ['climate', 'zone', 'temperature', 'sunlight', 'location', 'hardiness'],
      'Container Growing': ['container', 'pot', 'dwarf', 'patio', 'indoor', 'potted'],
      'Pruning': ['pruning', 'trimming', 'shaping', 'thinning', 'cutting'],
      'Harvesting': ['harvest', 'picking', 'ripe', 'maturity', 'season', 'storage'],
      'Propagation': ['propagation', 'grafting', 'cutting', 'seed', 'rooting'],
      'Tree Health': ['health', 'diagnosis', 'symptoms', 'treatment', 'recovery', 'vigor'],
      'Landscaping': ['landscape', 'design', 'ornamental', 'decoration', 'garden'],
      'General Citrus': ['citrus', 'tree', 'trees', 'fruit', 'fruits']
    };

    // Count keyword matches for each category
    let bestCategory = 'General';
    let maxMatches = 0;

    for (const [category, keywords] of Object.entries(categoryKeywords)) {
      const matches = keywords.filter(keyword => combinedText.includes(keyword)).length;
      if (matches > maxMatches) {
        maxMatches = matches;
        bestCategory = category;
      }
    }

    return bestCategory;
  };

  /**
   * Randomly select author from predefined list
   * Why this matters: Ensures author attribution is distributed across the team for content variety.
   */
  const selectRandomAuthor = (): string => {
    const authors = [
      'shaun-hinklein',
      'andy-mccotter-bicknell', 
      'cam-thompson',
      'kenny-keesee',
      'maribeth-daytona',
      'melanie-maecardeno'
    ];
    
    const randomIndex = Math.floor(Math.random() * authors.length);
    return authors[randomIndex];
  };

  const openGoogleSheets = async () => {
    const contentToCopy = isEditingContent ? editableContent : generatedContent;
    if (!contentToCopy) {
      alert('Please generate content first before logging to Google Sheets.');
      return;
    }

    setIsOpeningSheets(true);
    try {
      // Generate the new metadata fields
      const urlSlug = generateUrlSlug(keywordRow.keyword);
      const secondaryCategory = determineSecondaryCategory(keywordRow.keyword, contentToCopy);
      const author = selectRandomAuthor();

      // Prepare blog content data for logging with new fields (keyword removed)
      const blogData = {
        metaSeoTitle: metaSeoTitle || `${keywordRow.keyword} - Complete Guide`,
        metaDescription: metaDescription || `Comprehensive guide about ${keywordRow.keyword} with expert insights and actionable tips.`,
        htmlContent: contentToCopy,
        urlSlug: urlSlug,
        secondaryCategory: secondaryCategory,
        author: author
      };

      // Log data to your specific spreadsheet
      const result = await googleDocsService.appendToSpecificSpreadsheet('15u6QMH8AtfHN3UwHMUueJgRX11sCvsZsuEGbkPfPXm0', blogData);
      
      if (result.success) {
        // Show success message
        setSheetsSuccessMessage('Blog content logged to Google Sheets successfully!');
        setShowSheetsMessage(true);
        setTimeout(() => setShowSheetsMessage(false), 3000);
        
        // Open the specific Google Sheet URL you provided
        window.open('https://docs.google.com/spreadsheets/d/15u6QMH8AtfHN3UwHMUueJgRX11sCvsZsuEGbkPfPXm0/edit?gid=1144895816', '_blank');
      }
      
    } catch (error) {
      console.error('Error logging to Google Sheets:', error);
      
      // Show error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('Failed to save')) {
        alert('Unable to log to Google Sheets. Please check your Google account permissions and try again.');
      } else if (errorMessage.includes('Authentication')) {
        alert('Google authentication required. Please sign in to your Google account.');
      } else {
        alert(`Google Sheets error: ${errorMessage}. Please try again.`);
      }
    } finally {
      setIsOpeningSheets(false);
    }
  };

  /**
   * Insert variable using document.execCommand for proper undo support
   * Why this matters: Preserves undo stack by using browser's native text insertion method.
   */
  const insertVariable = (variableValue: string) => {
    const textarea = activePromptField === 'system' ? systemPromptRef.current : userPromptRef.current;
    
    if (textarea) {
      textarea.focus();
      
      // Use document.execCommand to insert text while preserving undo stack
      if (document.execCommand) {
        document.execCommand('insertText', false, ` ${variableValue} `);
      } else {
        // Fallback for browsers that don't support execCommand
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentValue = activePromptField === 'system' ? systemPrompt : userPrompt;
        
        const newValue = currentValue.substring(0, start) + ` ${variableValue} ` + currentValue.substring(end);
        
        if (activePromptField === 'system') {
          setSystemPrompt(newValue);
        } else {
          setUserPrompt(newValue);
        }
        
        // Set cursor position after the inserted variable
        setTimeout(() => {
          const newPosition = start + variableValue.length + 2;
          textarea.setSelectionRange(newPosition, newPosition);
          textarea.focus();
        }, 0);
      }
    }
    
    setShowVariablesMenu(false);
    setVariableSearchTerm('');
  };

  /**
   * Handle variables menu toggle with button positioning
   * Why this matters: Positions the popup menu to the right of the clicked button on desktop, or as a centered modal on mobile.
   */
  const handleVariablesMenuToggle = (promptField: 'system' | 'user') => {
    setActivePromptField(promptField);
    
    // Check if mobile
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      // On mobile, use centered modal positioning
      setVariablesButtonPosition({
        top: window.innerHeight / 2,
        left: window.innerWidth / 2
      });
    } else {
      // Desktop positioning logic
      const buttonRef = promptField === 'system' ? systemVariablesButtonRef.current : userVariablesButtonRef.current;
      if (buttonRef) {
        const rect = buttonRef.getBoundingClientRect();
        let topPosition = rect.top + window.scrollY;
        
        // For User Prompt, position menu much higher to ensure full visibility
        if (promptField === 'user') {
          const menuHeight = Math.min(window.innerHeight * 0.7, 600); // 70vh or 37.5rem max
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - rect.bottom;
          
          // Always position the menu above the button for User Prompt to ensure full visibility
          topPosition = rect.top + window.scrollY - menuHeight + 250; // Move down 15.625rem from the calculated position
          
          // Ensure it doesn't go above the viewport
          if (topPosition < window.scrollY + 20) {
            topPosition = window.scrollY + 20;
          }
        }
        
        setVariablesButtonPosition({
          top: topPosition,
          left: rect.right + window.scrollX + 12 // Position to the right with 0.75rem gap
        });
      }
    }
    
    setShowVariablesMenu(!showVariablesMenu);
    setVariableSearchTerm('');
  };

  /**
   * Copy content to clipboard with enhanced formatting
   * Why this matters: Provides the best possible copy experience with rich text formatting preserved across different applications.
   */
  const copyToClipboard = async () => {
    const contentToCopy = isEditingContent ? editableContent : generatedContent;
    try {
      await navigator.clipboard.writeText(contentToCopy);
      setShowCopiedMessage(true);
      setTimeout(() => setShowCopiedMessage(false), 2000);
    } catch (err) {
      console.error('Failed to copy content:', err);
      alert('Failed to copy content. Please select and copy manually.');
    }
  };

  /**
   * Toggle edit mode for generated content with CTA insertion capabilities
   * Why this matters: Allows users to edit content and insert CTAs at precise cursor positions.
   */
  const toggleEditMode = () => {
    if (isEditingContent) {
      console.log('💾 [toggleEditMode] Saving content from edit mode...');
      console.log('📝 [toggleEditMode] Raw editable content length:', editableContent.length);
      
      // Compact the HTML for saving (CTAs are already styled, no conversion needed)
      const compactedContent = compactHTMLForSaving(editableContent);
      console.log('🗜️ [toggleEditMode] Compacted content length:', compactedContent.length);
      console.log('📋 [toggleEditMode] Final content preview (first 200 chars):', compactedContent.substring(0, 200));
      
      setGeneratedContent(compactedContent);
      console.log('✅ [toggleEditMode] Generated content updated');
      
      // Auto-save the final content
      autoSaveContent(compactedContent, metaSeoTitle, metaDescription);
      // Update the original keyword row if callback provided
      if (onContentUpdate) {
        onContentUpdate(keywordRow.id, compactedContent);
      }
    } else {
      console.log('✏️ [toggleEditMode] Entering edit mode...');
      // Enter edit mode - sync editable content with current content and format for readability
      const formattedContent = formatHTMLForEditing(generatedContent);
      setEditableContent(formattedContent);
      console.log('📝 [toggleEditMode] Editable content set, length:', formattedContent.length);
      // Initialize edit history for undo/redo functionality
      initializeEditHistory(formattedContent);
    }
    setIsEditingContent(!isEditingContent);
    console.log('🔄 [toggleEditMode] Edit mode toggled to:', !isEditingContent);
  };

  /**
   * Format HTML content for better readability in edit mode
   * Why this matters: Breaks up condensed HTML into readable lines for easier editing.
   */
  const formatHTMLForEditing = (content: string): string => {
    if (!content) return content;

    let formatted = content
      // Add line breaks after closing tags
      .replace(/(<\/h[1-6]>)/g, '$1\n\n')
      .replace(/(<\/p>)/g, '$1\n\n')
      .replace(/(<\/div>)/g, '$1\n')
      .replace(/(<\/li>)/g, '$1\n')
      .replace(/(<\/ul>)/g, '$1\n\n')
      .replace(/(<\/ol>)/g, '$1\n\n')
      .replace(/(<\/table>)/g, '$1\n\n')
      .replace(/(<\/thead>)/g, '$1\n')
      .replace(/(<\/tbody>)/g, '$1\n')
      .replace(/(<\/tr>)/g, '$1\n')
      .replace(/(<\/blockquote>)/g, '$1\n\n')
      .replace(/(<\/section>)/g, '$1\n\n')
      .replace(/(<\/article>)/g, '$1\n\n')
      
      // Add line breaks before opening tags
      .replace(/(<h[1-6][^>]*>)/g, '\n$1')
      .replace(/(<p[^>]*>)/g, '\n$1')
      .replace(/(<ul[^>]*>)/g, '\n$1')
      .replace(/(<ol[^>]*>)/g, '\n$1')
      .replace(/(<li[^>]*>)/g, '\n  $1')
      .replace(/(<table[^>]*>)/g, '\n$1')
      .replace(/(<thead[^>]*>)/g, '\n  $1')
      .replace(/(<tbody[^>]*>)/g, '\n  $1')
      .replace(/(<tr[^>]*>)/g, '\n    $1')
      .replace(/(<th[^>]*>)/g, '$1')
      .replace(/(<td[^>]*>)/g, '$1')
      .replace(/(<blockquote[^>]*>)/g, '\n$1')
      .replace(/(<div[^>]*>)/g, '\n$1')
      
      // Clean up multiple consecutive newlines
      .replace(/\n{3,}/g, '\n\n')
      
      // Trim leading/trailing whitespace
      .trim();

    return formatted;
  };

  /**
   * Convert formatted HTML back to compact format for saving
   * Why this matters: Removes extra whitespace and formatting added for editing readability.
   */
  const compactHTMLForSaving = (content: string): string => {
    if (!content) return content;

    return content
      // Remove extra newlines and indentation
      .replace(/\n\s+/g, '\n')
      .replace(/\n{2,}/g, '\n')
      .replace(/>\s+</g, '><')
      .trim();
  };

  /**
   * Generate US Citrus sign-up URL with UTM tracking
   * Why this matters: Creates trackable sign-up links that help measure CTA performance by keyword and position.
   */
  const getQuizUrl = (position: string): string => {
    return generateQuizUtmUrl(keywordRow.keyword, position);
  };

  /**
   * Convert CTA shortcodes to full US Citrus styling with functional sign-up links
   * Why this matters: Transforms shortcodes into beautiful, production-ready CTA designs with trackable US Citrus sign-up URLs.
   */
  const convertShortcodesToFullStyling = (content: string): string => {
    if (!content || !generatedCTAs) return content;
    let processedContent = content;
    let hasCTA = false;
    Object.entries(generatedCTAs.cta_variants).forEach(([position, ctaData]) => {
      const shortcodePattern = new RegExp(ctaData.shortcode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      if (shortcodePattern.test(processedContent)) hasCTA = true;
      const quizUrl = getQuizUrl(position);
      const fullStyledCTA = getQuizCtaHtml(ctaData, quizUrl);
      processedContent = processedContent.replace(shortcodePattern, fullStyledCTA);
    });
    if (hasCTA && !processedContent.includes('quizCtaFade')) {
      processedContent = getQuizCtaStyles() + '\n' + processedContent;
    }
    return processedContent;
  };

  /**
   * Save current state to edit history
   * Why this matters: Enables undo/redo functionality by tracking content changes.
   */
  const saveToHistory = (content: string) => {
    const newHistory = editHistory.slice(0, historyIndex + 1);
    newHistory.push(content);
    setEditHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  /**
   * Initialize edit history when entering edit mode
   * Why this matters: Sets up the baseline for undo/redo operations.
   */
  const initializeEditHistory = (content: string) => {
    setEditHistory([content]);
    setHistoryIndex(0);
  };

  /**
   * Undo last change in edit mode
   * Why this matters: Allows users to revert unwanted changes without losing all their work.
   */
  const undoChange = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setEditableContent(editHistory[newIndex]);
      
      // Focus back to textarea
      setTimeout(() => {
        if (editableContentRef.current) {
          editableContentRef.current.focus();
        }
      }, 50);

      console.log(`↶ Undo: Reverted to history state ${newIndex}`);
    }
  };

  /**
   * Redo last undone change in edit mode
   * Why this matters: Allows users to restore changes they accidentally undid.
   */
  const redoChange = () => {
    if (historyIndex < editHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setEditableContent(editHistory[newIndex]);
      
      // Focus back to textarea
      setTimeout(() => {
        if (editableContentRef.current) {
          editableContentRef.current.focus();
        }
      }, 50);

      console.log(`↷ Redo: Advanced to history state ${newIndex}`);
    }
  };

  /**
   * Clear all CTAs from the current content
   * Why this matters: Provides a quick way to remove all inserted CTAs and start fresh.
   */
  const clearAllCTAs = () => {
    if (!generatedCTAs) return;
    const clearedContent = clearQuizCTAs(editableContent);
    saveToHistory(clearedContent);
    setEditableContent(clearedContent);
    setTimeout(() => {
      if (editableContentRef.current) {
        editableContentRef.current.focus();
      }
    }, 50);
  };

  /**
   * Insert full styled CTA at current cursor position
   * Why this matters: Allows precise placement of fully styled US Citrus CTAs within the content where the user clicks.
   */
  const insertCTAAtCursor = (position: 'beginning' | 'middle' | 'end') => {
    if (!generatedCTAs || !editableContentRef.current) return;
    const textarea = editableContentRef.current;
    const cursorPosition = textarea.selectionStart;
    const ctaData = generatedCTAs.cta_variants[position];
    const scrollTop = textarea.scrollTop;
    const scrollLeft = textarea.scrollLeft;
    const fullStyledCTA = getStyledCtaHtml(position);
    const ctaWithComment = `\n\n<!-- Quiz CTA: ${position.toUpperCase()} - ${ctaData.cta.headline} -->\n${fullStyledCTA}\n<!-- End Quiz CTA -->\n\n`;
    const beforeCursor = editableContent.substring(0, cursorPosition);
    const afterCursor = editableContent.substring(cursorPosition);
    const newContent = beforeCursor + ctaWithComment + afterCursor;
    saveToHistory(newContent);
    setEditableContent(newContent);
    setTimeout(() => {
      const newCursorPosition = cursorPosition + ctaWithComment.length;
      textarea.focus();
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
      textarea.scrollTop = scrollTop;
      textarea.scrollLeft = scrollLeft;
      setTimeout(() => {
        if (textarea.scrollTop !== scrollTop) {
          textarea.scrollTop = scrollTop;
          textarea.scrollLeft = scrollLeft;
        }
      }, 100);
    }, 50);
    setCtaCopySuccess(`${position}_inserted`);
    setTimeout(() => setCtaCopySuccess(''), 2000);
  };

  /**
   * Count inserted CTAs in current content
   * Why this matters: Provides feedback on how many CTAs have been inserted.
   */
  const countInsertedCTAs = (): number => {
    return countQuizCTAs(editableContent);
  };

  /**
   * Get display content with CTA styling applied
   * Why this matters: Shows styled CTAs in the preview while maintaining shortcodes in edit mode.
   */
  const getDisplayContent = (): string => {
    if (isEditingContent) {
      // In edit mode, show the editable content directly (now contains full HTML CTAs)
      return editableContent;
    } else {
      // In display mode, return the saved content directly (already styled during save)
      return generatedContent;
    }
  };



  /**
   * Generate content using AI service (for new generation)
   * Why this matters: Creates fresh content and properly extracts meta fields from JSON response
   */
  const generateContent = async () => {
    console.log('🚀 [BlogContentActionModal] Starting generateContent...');
    console.log('🔍 [BlogContentActionModal] Brand kit available:', !!brandKit);
    console.log('🔍 [BlogContentActionModal] Brand kit data:', brandKit);
    
    if (!brandKit) {
      console.log('❌ [BlogContentActionModal] No brand kit found');
      alert('Please configure your Brand Kit first in the Brand Kit page.');
      return;
    }

    // Load sitemap data from localStorage
    let sitemapData = null;
    try {
      const stored = localStorage.getItem('us_citrus_sitemap_data');
      if (stored) {
        const sitemaps = JSON.parse(stored);
        // Flatten all sitemap URLs into a single array for content generation
        const allUrls = sitemaps.flatMap((sitemap: any) => 
          sitemap.urls.map((url: any) => ({
            title: url.title,
            description: url.description,
            url: url.url
          }))
        );
        sitemapData = allUrls;
        console.log(`🗺️ [BlogContentActionModal] Loaded ${allUrls.length} URLs from ${sitemaps.length} sitemaps for internal linking`);
      }
    } catch (error) {
      console.warn('Failed to load sitemap data:', error);
    }

    setIsGenerating(true);
    try {
      console.log('🔍 [BlogContentActionModal] Original system prompt:', systemPrompt);
      console.log('🔍 [BlogContentActionModal] Original user prompt:', userPrompt);
      
      // Replace liquid variables in prompts
      const processedSystemPrompt = processLiquidVariables(systemPrompt, brandKit);
      const processedUserPrompt = processLiquidVariables(userPrompt, brandKit);
      
      console.log('✅ [BlogContentActionModal] Processed system prompt:', processedSystemPrompt);
      console.log('✅ [BlogContentActionModal] Processed user prompt:', processedUserPrompt);

      // Create proper post_context structure that the backend expects
      const postContext = {
        title: `${keywordRow.keyword} - Comprehensive Guide`,
        content: keywordRow.output || '',
        pain_point: `Content creation and optimization for ${keywordRow.keyword}`,
        content_opportunity: `Create comprehensive, AEO-optimized content for ${keywordRow.keyword} that outranks competitors`,
        audience_summary: brandKit.idealCustomerProfile || 'Business professionals seeking solutions'
      };

      // Determine backend URL based on environment
      // Why this matters: Ensures production deployments use the correct backend URL
      const backendUrl = process.env.NODE_ENV === 'production' 
        ? 'https://us-citrus-ai-backend.vercel.app'
        : 'http://localhost:3001';
      
      const requestBody = {
        post_context: postContext,
        brand_kit: brandKit,
        sitemap_data: sitemapData,
        system_prompt: processedSystemPrompt,
        user_prompt: processedUserPrompt
      };
      
      console.log('📡 [BlogContentActionModal] API Request Body:', requestBody);
      console.log('📡 [BlogContentActionModal] Backend URL:', backendUrl);

      // Use regular content generation with enhanced post-processing CTAs
      console.log('🚀 [BlogContentActionModal] Using content generation with enhanced contextual CTAs...');
      
      const apiResult = await makeApiRequest(
        API_ENDPOINTS.blogCreatorGenerateContent,
        {
          method: 'POST',
          body: JSON.stringify({
            keyword: keywordRow.keyword,
            brand_kit: brandKit,
            sitemap_data: sitemapData,
            system_prompt: processedSystemPrompt,
            user_prompt: processedUserPrompt,
            use_default_prompts: false, // We're providing custom prompts
            target_audience: '',
            content_length: 'medium',
            focus_areas: []
          }),
        }
      );

      if (!apiResult.success) {
        throw new Error(apiResult.error || apiResult.message || 'Failed to generate content');
      }

      const data = apiResult.data!;
      console.log('📥 [BlogContentActionModal] API Response:', data);
      
      // Handle the response - extract content
      let contentResult = '';
      if (data.content) {
        contentResult = data.content;
      } else {
        console.log('❌ [BlogContentActionModal] No content found in response structure:', data);
        throw new Error('No content found in API response');
      }
      
      console.log('✅ [BlogContentActionModal] Final content result:', contentResult);
      
      // Parse the AI response to extract all fields
      const parsedResponse = parseAIResponse(contentResult);
      
      // Enhance with contextual CTAs if enabled
      let finalContent = parsedResponse.content;
      if (enableContextualCtas && finalContent && finalContent.length > 100) {
        try {
          setIsEnhancingWithCtas(true);
          console.log('🎯 [BlogContentActionModal] Enhancing content with contextual CTAs...');
          
          const targetKeyword = keywordRow.keyword;
          const campaignType = 'blog_creator';
          
          // Get sitemap data from localStorage for intelligent URL selection
          const getSitemapData = () => {
            try {
              const stored = localStorage.getItem('us_citrus_sitemap_data');
              return stored ? JSON.parse(stored) : null;
            } catch (error) {
              console.warn('Failed to load sitemap data:', error);
              return null;
            }
          };

          // Use simple, reliable CTA service with sitemap-aware URL selection
          const ctaResponse = await fetch(API_ENDPOINTS.enhanceWithSimpleCtas, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              content: finalContent,
              contentFormat: 'html',
              targetKeyword,
              campaignType,
              maxCtasPerArticle: 3,
              sitemapData: getSitemapData()
            })
          });
          
          const ctaResult = await ctaResponse.json();
          
          if (ctaResult.success) {
            finalContent = ctaResult.enhancedContent;
            setCtaEnhancementResult(ctaResult.insertionAnalytics);
            console.log(`✅ [BlogContentActionModal] Enhanced content with ${ctaResult.insertionAnalytics.totalCtasInserted} contextual CTAs`);
          }
        } catch (error) {
          console.warn('⚠️ [BlogContentActionModal] Failed to enhance content with contextual CTAs, using original content:', error);
          // Continue with original content if CTA enhancement fails
        } finally {
          setIsEnhancingWithCtas(false);
        }
      }
      
      setGeneratedContent(finalContent);
      setEditableContent(finalContent);
      setIsEditingContent(false);
      
      // Set meta fields from API response or parsed content
      // Why this matters: Uses meta fields from backend API response if available, otherwise from parsed content
      const metaTitle = data.metaSeoTitle || parsedResponse.metaSeoTitle || '';
      const metaDesc = data.metaDescription || parsedResponse.metaDescription || '';
      
      if (metaTitle || metaDesc) {
        console.log('✅ Setting meta fields from API response:', {
          title: metaTitle,
          description: metaDesc
        });
        setMetaSeoTitle(metaTitle);
        setMetaDescription(metaDesc);
        
        // Save meta fields to localStorage for persistence
        try {
          localStorage.setItem(`us_citrus_blog_meta_${keywordRow.id}`, JSON.stringify({
            metaSeoTitle: metaTitle,
            metaDescription: metaDesc
          }));
        } catch (error) {
          console.log('⚠️ Could not save meta fields to localStorage:', error);
        }
      }

      // Auto-save the newly generated content (use enhanced content)
      autoSaveContent(finalContent, metaTitle, metaDesc);

      // Update the original keyword row if callback provided
      if (onContentUpdate) {
        onContentUpdate(keywordRow.id, finalContent);
      }

    } catch (error) {
      console.error('Error generating content:', error);
      
      // Fallback content
      const fallbackContent = `
        <h1>${keywordRow.keyword.charAt(0).toUpperCase() + keywordRow.keyword.slice(1)} Guide</h1>
        
        <h2>Executive Summary</h2>
        <p>This comprehensive guide provides expert insights and proven strategies for ${keywordRow.keyword}, leveraging US Citrus's expert knowledge and resources.</p>
        
        <h2>Key Challenges</h2>
        <p>Understanding the critical challenges around ${keywordRow.keyword} that modern businesses face and how to address them effectively.</p>
        
        <h2>Strategic Framework</h2>
        <p>Core strategies and best practices for ${keywordRow.keyword}:</p>
        <ul>
          <li>Data-driven insights and analytics</li>
          <li>Proven methodologies and tactics</li>
          <li>Implementation roadmaps</li>
          <li>Performance measurement</li>
        </ul>
        
        <h2>Implementation Strategy</h2>
        <p>Step-by-step approach to implementing effective ${keywordRow.keyword} strategies within your organization.</p>
        
        <h2>Success Metrics</h2>
        <p>Key performance indicators and metrics to track success and ROI for your ${keywordRow.keyword} initiatives.</p>
        
        <p><strong>Ready to implement these strategies?</strong> <a href="${brandKit?.ctaDestination || 'https://uscitrusnursery.com'}" target="_blank">${brandKit?.ctaText || 'Find Your Perfect Tree'}</a></p>
      `;
      
      // Use parseAIResponse for fallback content too
      const parsedFallback = parseAIResponse(fallbackContent);
      const fallbackMetaTitle = `${keywordRow.keyword.charAt(0).toUpperCase() + keywordRow.keyword.slice(1)} - Complete Guide | US Citrus`;
      const fallbackMetaDescription = `Comprehensive guide to ${keywordRow.keyword} with expert insights and proven strategies. Learn practical approaches to drive results with US Citrus.`;
      
      setGeneratedContent(parsedFallback.content);
      setEditableContent(parsedFallback.content);
      setIsEditingContent(false);
      setMetaSeoTitle(fallbackMetaTitle);
      setMetaDescription(fallbackMetaDescription);

      // Auto-save the fallback content
      autoSaveContent(parsedFallback.content, fallbackMetaTitle, fallbackMetaDescription);

      // Update the original keyword row if callback provided
      if (onContentUpdate) {
        onContentUpdate(keywordRow.id, parsedFallback.content);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * Process liquid variables in text using brand kit
   * Why this matters: Replaces brand kit variables in prompts before sending to AI
   */
  const processLiquidVariables = (text: string, brandKit: BrandKit): string => {
    let processed = text;
    
    // Replace standard brand kit variables
    processed = processed.replace(/\{\{\s*brand_kit\.url\s*\}\}/g, brandKit.url || '');
    processed = processed.replace(/\{\{\s*brand_kit\.about_brand\s*\}\}/g, brandKit.aboutBrand || '');
    processed = processed.replace(/\{\{\s*brand_kit\.ideal_customer_profile\s*\}\}/g, brandKit.idealCustomerProfile || '');
    processed = processed.replace(/\{\{\s*brand_kit\.competitors\s*\}\}/g, brandKit.competitors || '');
    processed = processed.replace(/\{\{\s*brand_kit\.brand_point_of_view\s*\}\}/g, brandKit.brandPointOfView || '');
    processed = processed.replace(/\{\{\s*brand_kit\.author_persona\s*\}\}/g, brandKit.authorPersona || '');
    processed = processed.replace(/\{\{\s*brand_kit\.tone_of_voice\s*\}\}/g, brandKit.toneOfVoice || '');
    processed = processed.replace(/\{\{\s*brand_kit\.header_case_type\s*\}\}/g, brandKit.headerCaseType || '');
    processed = processed.replace(/\{\{\s*brand_kit\.writing_rules\s*\}\}/g, brandKit.writingRules || '');
    // Use random CTA instead of brand kit CTA to maintain even distribution
    processed = processed.replace(/\{\{\s*brand_kit\.cta_text\s*\}\}/g, getRandomCTAAnchorText());
    processed = processed.replace(/\{\{\s*brand_kit\.cta_destination\s*\}\}/g, brandKit.ctaDestination || '');
    
    // Replace custom variables
    if (brandKit.customVariables) {
      Object.keys(brandKit.customVariables).forEach(key => {
        const regex = new RegExp(`\\{\\{\\s*brand_kit\\.${key}\\s*\\}\\}`, 'g');
        processed = processed.replace(regex, brandKit.customVariables[key] || '');
      });
    }
    
    return processed;
  };

  /**
   * Run content generation workflow again
   * Why this matters: Allows users to regenerate content with new prompts or settings
   */
  const runWorkflowAgain = async () => {
    console.log('🚀 [BlogContentActionModal] Starting runWorkflowAgain...');
    console.log('🔍 [BlogContentActionModal] Brand kit available:', !!brandKit);
    
    if (!brandKit) {
      console.log('❌ [BlogContentActionModal] No brand kit found');
      alert('Please configure your Brand Kit first in the Brand Kit page.');
      return;
    }

    try {
      // Process liquid variables in prompts
      const processedSystemPrompt = processLiquidVariables(systemPrompt, brandKit);
      const processedUserPrompt = processLiquidVariables(userPrompt, brandKit);
      
      console.log('✅ [BlogContentActionModal] Processed system prompt:', processedSystemPrompt);
      console.log('✅ [BlogContentActionModal] Processed user prompt:', processedUserPrompt);

      // Determine backend URL based on environment
      const backendUrl = process.env.NODE_ENV === 'production' 
        ? 'https://us-citrus-ai-backend.vercel.app'
        : 'http://localhost:3003';

      // Update status to running BEFORE making the API call
      if (onStatusUpdate) {
        onStatusUpdate(keywordRow.id, 'running');
      }

      // Close modal to let user see the progress in the table
      onClose();

      // Call the blog creator API with custom prompts
      const response = await fetch(`${backendUrl}/api/blog-creator/generate-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          keyword: keywordRow.keyword,
          content_length: 'medium',
          brand_kit: brandKit,
          system_prompt: processedSystemPrompt,
          user_prompt: processedUserPrompt
        })
      });

      if (!response.ok) {
        throw new Error('Failed to regenerate content');
      }

      const responseData = await response.json();
      const { data: result } = responseData;
      
      console.log('✅ [BlogContentActionModal] Workflow regeneration completed');

      // Update the keyword row with new content
      if (onContentUpdate) {
        onContentUpdate(keywordRow.id, result.content || '');
      }
      
      if (onStatusUpdate) {
        onStatusUpdate(keywordRow.id, 'completed');
      }

      // Auto-save to blog history
      const updatedKeywordRow = {
        ...keywordRow,
        status: 'completed' as const,
        output: result.content || '',
        metadata: result.metadata || keywordRow.metadata
      };
      autoSaveBlogIfReady(updatedKeywordRow);

    } catch (error) {
      console.error('❌ [BlogContentActionModal] Workflow regeneration failed:', error);
      
      if (onStatusUpdate) {
        onStatusUpdate(keywordRow.id, 'error');
      }
      
      alert(`Failed to regenerate content: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  /**
   * Publish content to Shopify or other CMS
   * Why this matters: Enables direct publishing to Shopify blog for citrus tree articles
   * 
   * Uses real Shopify API if cms_type is 'shopify' and backend is configured,
   * otherwise falls back to demo mode.
   */
  const publishToCustomCMS = async (status: 'draft' | 'published' = 'draft') => {
    if (!generatedContent || !metaSeoTitle) {
      alert('Please generate content first');
      return;
    }

    setIsPublishing(true);
    setPublishResult(null);

    try {
      console.log('📰 Publishing content to CMS:', customCMSConfig);

      // Use centralized API config
      // Why this matters: Ensures consistent API URL across environments
      const { API_BASE_URL } = await import('../config/api');
      
      // Determine which endpoint to use based on CMS type
      const isShopify = customCMSConfig.cms_type.toLowerCase() === 'shopify';
      const endpoint = isShopify 
        ? `${API_BASE_URL}/api/content/publish-to-shopify`
        : `${API_BASE_URL}/api/content/publish-to-cms`;
      
      console.log(`🎯 Using endpoint: ${endpoint}`);
      
      // Get tree info from keywordRow if available (for citrus tree articles)
      const treeName = (keywordRow as any)?.treeName || keywordRow?.keyword || '';
      const category = (keywordRow as any)?.category || '';
        
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: metaSeoTitle,
          content: generatedContent,
          meta_title: metaSeoTitle,
          meta_description: metaDescription,
          api_endpoint: customCMSConfig.api_endpoint,
          api_key: customCMSConfig.api_key,
          cms_type: customCMSConfig.cms_type,
          status: status,
          // Include citrus tree specific data
          treeName: treeName,
          category: category,
          tags: category ? `citrus trees, ${category.toLowerCase()}` : 'citrus trees'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.message || errorData.error || 'Failed to publish content');
      }

      const data = await response.json();
      
      setPublishResult({
        success: true,
        ...data.publication,
        demo_mode: data.demo_mode
      });

      console.log('✅ Publish successful:', data);
      
      // Show success message with article URL if available
      if (data.publication?.url && !data.demo_mode) {
        console.log(`🔗 Article published: ${data.publication.url}`);
      }

    } catch (error) {
      console.error('Publication error:', error);
      setPublishResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsPublishing(false);
    }
  };

  /**
   * Show confirmation dialog before clearing content
   * Why this matters: Prevents accidental deletion of generated content
   */
  const clearGeneratedContent = () => {
    setShowClearConfirmation(true);
  };

  /**
   * Clear all generated content and reset to original state
   * Why this matters: Allows users to start fresh without closing and reopening the modal
   */
  const confirmClearContent = () => {
    setGeneratedContent('');
    setEditableContent('');
    setIsEditingContent(false);
    setMetaSeoTitle('');
    setMetaDescription('');
    setShowClearConfirmation(false);
    // Clear auto-save data
    clearAutoSaveData();
    if (onContentUpdate) {
      onContentUpdate(keywordRow.id, '');
    }
  };

  /**
   * Cancel the clear confirmation dialog
   * Why this matters: Allows users to cancel accidental clear attempts
   */
  const cancelClearContent = () => {
    setShowClearConfirmation(false);
  };

  /**
   * Cycle CTA button text to a random different option (copied from CTACreatorPage)
   * Why this matters: Allows users to test different CTA buttons without regenerating entire CTAs.
   */
  const cycleCTAButton = (position: 'beginning' | 'middle' | 'end') => {
    if (!generatedCTAs) return;
    const currentButton = generatedCTAs.cta_variants[position].cta.action_button;
    const currentIndex = QUIZ_BUTTON_OPTIONS.indexOf(currentButton);
    const nextIndex = (currentIndex + 1) % QUIZ_BUTTON_OPTIONS.length;
    const newButton = QUIZ_BUTTON_OPTIONS[nextIndex];
    const updated = {
      ...generatedCTAs,
      cta_variants: {
        ...generatedCTAs.cta_variants,
        [position]: {
          ...generatedCTAs.cta_variants[position],
          cta: {
            ...generatedCTAs.cta_variants[position].cta,
            action_button: newButton
          }
        }
      }
    };
    setGeneratedCTAs(updated);
    autoSaveCTAs(updated);
  };

  const generateCTAs = () => {
    if (!generatedContent.trim()) {
      setCtaError('Please generate content first');
      return;
    }
    setIsGeneratingCTAs(true);
    setCtaError('');
    setTimeout(() => {
      const idx = generatedCTAs ? quizVariantIndex + 1 : 0;
      const ctaData = generateQuizCTAs(idx);
      setQuizVariantIndex(idx);
      setGeneratedCTAs(ctaData as CTAGenerationResult);
      setIsGeneratingCTAs(false);

      try {
        localStorage.setItem(`quiz_ctas_${keywordRow.id}`, JSON.stringify({ ctas: ctaData, variantIndex: idx }));
      } catch {}
    }, 400);
  };

  const getStyledCtaHtml = (position: 'beginning' | 'middle' | 'end'): string => {
    if (!generatedCTAs) return '';
    const ctaData = generatedCTAs.cta_variants[position];
    const quizUrl = getQuizUrl(position);
    return getQuizCtaHtml(ctaData, quizUrl);
  };

  /**
   * Copy CTA content to clipboard (adapted from CTACreatorPage)
   * Why this matters: Provides reliable copying functionality for CTA shortcodes and content.
   */
  const copyCtaToClipboard = async (text: string, position: string) => {
    if (!text || text.trim() === '') {
      setCtaError('No content available to copy');
      return;
    }

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        setCtaCopySuccess(position);
        setTimeout(() => setCtaCopySuccess(''), 2000);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          setCtaCopySuccess(position);
          setTimeout(() => setCtaCopySuccess(''), 2000);
        } else {
          throw new Error('Copy command failed');
        }
      }
    } catch (error) {
      console.error('Failed to copy CTA:', error);
      setCtaError('Failed to copy content. Please manually select and copy the content.');
      setTimeout(() => setCtaError(''), 5000);
    }
  };

  /**
   * Dismiss VoC Kit ready notification
   * Why this matters: Allows users to hide the success notification once acknowledged.
   */
  const dismissVocKitReady = () => {
    try {
      localStorage.setItem('us_citrus_voc_kit_ready_dismissed', 'true');
      setVocKitReadyDismissed(true);
    } catch (error) {
      console.error('Failed to dismiss VoC Kit ready notification:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Auto-save indicator */}
      {autoSaveStatus && (
        <div style={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          zIndex: 10001,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1rem',
          backgroundColor: 'white',
          border: '0.0625rem solid #e5e7eb',
          borderRadius: '0.5rem',
          boxShadow: '0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1), 0 0.125rem 0.25rem -0.0625rem rgba(0, 0, 0, 0.06)',
          color: autoSaveStatus === 'saving' ? '#6b7280' : '#10b981',
          fontSize: '0.875rem',
          fontWeight: '500',
          pointerEvents: 'none'
        }}>
          {autoSaveStatus === 'saving' ? (
            <>
              <Clock className="animate-spin" style={{ width: '0.75rem', height: '0.75rem' }} />
              Auto-saving prompts...
            </>
          ) : (
            <>
              <CheckCircle size={14} />
              Prompts auto-saved
            </>
          )}
        </div>
      )}

      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '1rem'
        }}
      >
        <div style={{
          backgroundColor: 'white',
          borderRadius: '0.75rem',
          width: '100%',
          maxWidth: '95vw', // Mobile-first: full width with small margin
          height: '95vh', // Slightly smaller height for mobile
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
        className="content-creation-modal"
        >
          {/* Header */}
          <div 
            className="content-modal-header"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1.5rem',
              borderBottom: '0.0625rem solid #e5e7eb',
              backgroundColor: '#fafafa',
              position: 'relative'
            }}
          >
            <div>
              <h2 style={{ fontWeight: '600', margin: 0, color: '#111827' }}>
                Blog Content Actions
              </h2>
              <p style={{ color: '#6b7280' }}>
                Edit, publish, and manage your generated content for "{keywordRow.keyword}"
              </p>
            </div>
            <button
              onClick={onClose}
              className="content-modal-close"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.15s ease'
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <X size={24} />
            </button>
          </div>

          {/* Content Layout - Exactly like ContentCreationModal */}
          <div className="content-modal-layout" style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
            {/* Left Panel - CTA Generation Interface */}
            <div className="content-modal-panel" style={{ 
              padding: '1.5rem', 
              overflowY: 'auto',
              borderRight: '0.0625rem solid #e5e7eb'
            }}>
              {/* Quiz CTA Generation */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <div style={{
                padding: '0.5rem',
                background: '#4E960C',
                borderRadius: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Target size={24} style={{ color: '#FFD93D' }} />
              </div>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0, color: '#374151' }}>
                Generate Quiz CTAs
              </h3>
            </div>

            <p style={{ fontSize: '0.875rem', color: '#64748b', margin: '0 0 1.5rem 0', lineHeight: '1.5' }}>
              {generatedCTAs 
                ? 'Regenerate with fresh copy variations.'
                : 'Create beautiful CTAs that promote the US Citrus quiz.'
              }
            </p>

            <button
              onClick={generateCTAs}
              disabled={isGeneratingCTAs || !generatedContent.trim()}
              style={{
                width: '100%',
                padding: '1rem 1.5rem',
                background: (isGeneratingCTAs || !generatedContent.trim()) ? '#9ca3af' : '#4E960C',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '1rem',
                fontWeight: '700',
                cursor: (isGeneratingCTAs || !generatedContent.trim()) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                transition: 'all 0.2s ease',
                marginBottom: '1.5rem'
              }}
            >
              {isGeneratingCTAs ? (
                <>
                  <div style={{
                    width: '1rem', height: '1rem',
                    border: '0.125rem solid transparent',
                    borderTop: '0.125rem solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Generating Quiz CTAs...
                </>
              ) : (
                <>
                  <Target size={20} strokeWidth={3} />
                  {generatedCTAs ? 'Regenerate Quiz CTAs' : 'Generate Quiz CTAs'}
                </>
              )}
            </button>

            {ctaError && (
              <div style={{ marginBottom: '1.5rem', padding: '0.75rem', backgroundColor: '#fee2e2', borderRadius: '0.5rem', fontSize: '0.875rem', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <AlertCircle size={16} />
                {ctaError}
              </div>
            )}

            {generatedCTAs && (
              <QuizCtaPreviewCards
                ctaVariants={generatedCTAs.cta_variants}
                compact
              />
            )}

            {/* Divider */}
            <div style={{ borderTop: '1px solid #e5e7eb', margin: '0 0 2rem 0' }} />

              {/* Image Generation Interface */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <div style={{
                  padding: '0.5rem',
                  backgroundColor: '#EBF212',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <ImageIcon size={24} style={{ color: 'black' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0, color: '#374151' }}>
                    Generate Images
                  </h3>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>
                    Powered by {productImageKey ? MODEL_DISPLAY_NAMES.gptImage : MODEL_DISPLAY_NAMES.geminiFlashImage}
                  </p>
                </div>
              </div>

              <p style={{ 
                fontSize: '0.875rem', 
                color: '#64748b', 
                margin: '0 0 1.5rem 0',
                lineHeight: '1.5'
              }}>
                Generate 3 unique images (beginning, middle, ending) for your article. Use the "Insert" button to add them to the content editor.
              </p>

              {/* Image Generation Button */}
              <button
                onClick={generateImages}
                disabled={isGeneratingImages || !generatedContent.trim()}
                style={{
                  width: '100%',
                  padding: '1rem 1.5rem',
                  backgroundColor: (isGeneratingImages || !generatedContent.trim()) ? '#9ca3af' : '#EBF212',
                  color: (isGeneratingImages || !generatedContent.trim()) ? 'white' : 'black',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: (isGeneratingImages || !generatedContent.trim()) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  transition: 'all 0.2s ease',
                  marginBottom: '1.5rem'
                }}
              >
                {isGeneratingImages ? (
                  <>
                    <div style={{
                      width: '1rem',
                      height: '1rem',
                      border: '0.125rem solid transparent',
                      borderTop: '0.125rem solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Generating Images...
                  </>
                ) : (
                  <>
                    <Sparkles size={20} strokeWidth={2} />
                    {generatedImages.length > 0 ? 'Regenerate Images' : 'Generate Images'}
                  </>
                )}
              </button>

              {imageError && (
                <div style={{
                  marginBottom: '1.5rem',
                  padding: '0.75rem',
                  backgroundColor: '#fee2e2',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  color: '#dc2626',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <AlertCircle size={16} />
                  {imageError}
                </div>
              )}

              {/* Generated Images List */}
              {generatedImages.length > 0 && (
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  {generatedImages.map((img, index) => (
                    <div key={index} style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.75rem',
                      padding: '1rem',
                      backgroundColor: '#fafafa'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '0.75rem'
                      }}>
                        <div style={{ 
                          fontSize: '0.875rem', 
                          fontWeight: '600', 
                          color: '#374151',
                          textTransform: 'capitalize' 
                        }}>
                          {img.placement} Image
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          {/* Insert Button - Only shown in edit mode */}
                          <button
                            onClick={() => insertImageAtCursor(img)}
                            disabled={!isEditingContent}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: imageCopySuccess === `${img.placement}_inserted` ? '#dcfce7' : (isEditingContent ? '#dbeafe' : '#f3f4f6'),
                              color: imageCopySuccess === `${img.placement}_inserted` ? '#16a34a' : (isEditingContent ? '#1e40af' : '#9ca3af'),
                              border: `1px solid ${imageCopySuccess === `${img.placement}_inserted` ? '#bbf7d0' : (isEditingContent ? '#93c5fd' : '#d1d5db')}`,
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              cursor: isEditingContent ? 'pointer' : 'not-allowed',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              transition: 'all 0.2s ease'
                            }}
                            title={isEditingContent ? `Insert ${img.placement} image at cursor` : 'Enter Edit Mode to insert images'}
                          >
                            {imageCopySuccess === `${img.placement}_inserted` ? (
                              <>
                                <CheckCircle size={12} />
                                Inserted!
                              </>
                            ) : (
                              <>
                                <Target size={12} />
                                Insert
                              </>
                            )}
                          </button>
                          {/* Copy HTML Button */}
                          <button
                            onClick={() => copyImageHtml(img)}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: imageCopySuccess === img.placement ? '#dcfce7' : 'white',
                              color: imageCopySuccess === img.placement ? '#16a34a' : '#374151',
                              border: '1px solid #e5e7eb',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {imageCopySuccess === img.placement ? (
                              <>
                                <CheckCircle size={12} />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy size={12} />
                                Copy HTML
                              </>
                            )}
                          </button>
                          {/* Download PNG Button */}
                          <button
                            onClick={() => downloadImageAsPng(img)}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: imageDownloadSuccess === img.placement ? '#dcfce7' : 'white',
                              color: imageDownloadSuccess === img.placement ? '#16a34a' : '#374151',
                              border: '1px solid #e5e7eb',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              transition: 'all 0.2s ease'
                            }}
                            title="Download this image as a PNG file"
                          >
                            {imageDownloadSuccess === img.placement ? (
                              <>
                                <CheckCircle size={12} />
                                Saved!
                              </>
                            ) : (
                              <>
                                <Download size={12} />
                                Download
                              </>
                            )}
                          </button>
                          {/* Regenerate Button */}
                          <button
                            onClick={() => regenerateSingleImage(img.placement)}
                            disabled={regeneratingPlacement !== null}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: regeneratingPlacement === img.placement ? '#fef3c7' : '#f3f4f6',
                              color: regeneratingPlacement === img.placement ? '#92400e' : '#374151',
                              border: `1px solid ${regeneratingPlacement === img.placement ? '#fcd34d' : '#e5e7eb'}`,
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              cursor: regeneratingPlacement !== null ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              transition: 'all 0.2s ease',
                              opacity: regeneratingPlacement !== null && regeneratingPlacement !== img.placement ? 0.5 : 1
                            }}
                            title={`Regenerate ${img.placement} image`}
                          >
                            <RefreshCw 
                              size={12} 
                              style={{ 
                                animation: regeneratingPlacement === img.placement ? 'spin 1s linear infinite' : 'none' 
                              }} 
                            />
                            {regeneratingPlacement === img.placement ? 'Regenerating...' : 'Regenerate'}
                          </button>
                          {/* Undo Button */}
                          <button
                            onClick={undoChange}
                            disabled={!isEditingContent || historyIndex <= 0}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#f3f4f6',
                              color: (!isEditingContent || historyIndex <= 0) ? '#9ca3af' : '#374151',
                              border: '1px solid #e5e7eb',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              cursor: (!isEditingContent || historyIndex <= 0) ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              transition: 'all 0.2s ease',
                              opacity: (!isEditingContent || historyIndex <= 0) ? 0.5 : 1
                            }}
                            title={historyIndex > 0 ? 'Undo last change' : 'Nothing to undo'}
                          >
                            <Undo2 size={12} />
                            Undo
                          </button>
                          {/* Redo Button */}
                          <button
                            onClick={redoChange}
                            disabled={!isEditingContent || historyIndex >= editHistory.length - 1}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#f3f4f6',
                              color: (!isEditingContent || historyIndex >= editHistory.length - 1) ? '#9ca3af' : '#374151',
                              border: '1px solid #e5e7eb',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              cursor: (!isEditingContent || historyIndex >= editHistory.length - 1) ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              transition: 'all 0.2s ease',
                              opacity: (!isEditingContent || historyIndex >= editHistory.length - 1) ? 0.5 : 1
                            }}
                            title={historyIndex < editHistory.length - 1 ? 'Redo last change' : 'Nothing to redo'}
                          >
                            <Redo2 size={12} />
                            Redo
                          </button>
                        </div>
                      </div>

                      {/* Image container with skeleton loading */}
                      <div style={{
                        borderRadius: '0.5rem',
                        overflow: 'hidden',
                        border: '1px solid #e5e7eb',
                        marginBottom: '0.75rem',
                        backgroundColor: '#f3f4f6',
                        position: 'relative',
                        minHeight: regeneratingPlacement === img.placement ? '200px' : 'auto'
                      }}>
                        {regeneratingPlacement === img.placement ? (
                          /* Skeleton Loading Effect */
                          <div style={{
                            width: '100%',
                            height: '200px',
                            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                            backgroundSize: '200% 100%',
                            animation: 'shimmer 1.5s infinite',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '0.5rem',
                              color: '#9ca3af'
                            }}>
                              <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
                              <span style={{ fontSize: '0.875rem' }}>Regenerating image...</span>
                            </div>
                          </div>
                        ) : (
                          <img 
                            src={img.imageUrl || `data:${img.mimeType};base64,${img.base64Data}`} 
                            alt={img.altText}
                            style={{
                              width: '100%',
                              height: 'auto',
                              display: 'block'
                            }}
                          />
                        )}
                      </div>
                      
                      <div style={{ 
                        fontSize: '0.75rem', 
                        color: '#6b7280', 
                        fontStyle: 'italic',
                        lineHeight: '1.4'
                      }}>
                        "{img.altText}"
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Divider */}
              <div style={{ 
                borderTop: '1px solid #e5e7eb', 
                margin: '2rem 0' 
              }} />

              {/* Infographic Generation Interface */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <div style={{
                  padding: '0.5rem',
                  backgroundColor: '#10b981',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <BarChart3 size={24} style={{ color: 'white' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0, color: '#374151' }}>
                    Generate Infographic
                  </h3>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>
                    Powered by {productImageKey ? MODEL_DISPLAY_NAMES.gptImage : MODEL_DISPLAY_NAMES.geminiFlashImage}
                  </p>
                </div>
              </div>

              <p style={{ 
                fontSize: '0.875rem', 
                color: '#64748b', 
                margin: '0 0 1.5rem 0',
                lineHeight: '1.5'
              }}>
                Generate a branded infographic summarizing key facts, care guide, and benefits of {keywordRow.keyword}.
              </p>

              {/* Infographic Generation Button */}
              <button
                onClick={generateInfographic}
                disabled={isGeneratingInfographic || !generatedContent.trim()}
                style={{
                  width: '100%',
                  padding: '1rem 1.5rem',
                  backgroundColor: (isGeneratingInfographic || !generatedContent.trim()) ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: (isGeneratingInfographic || !generatedContent.trim()) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  transition: 'all 0.2s ease',
                  marginBottom: '1.5rem'
                }}
              >
                {isGeneratingInfographic ? (
                  <>
                    <div style={{
                      width: '1rem',
                      height: '1rem',
                      border: '0.125rem solid transparent',
                      borderTop: '0.125rem solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Generating Infographic...
                  </>
                ) : (
                  <>
                    <Sparkles size={20} strokeWidth={2} />
                    {generatedInfographic ? 'Regenerate Infographic' : 'Generate Infographic'}
                  </>
                )}
              </button>

              {infographicError && (
                <div style={{
                  marginBottom: '1.5rem',
                  padding: '0.75rem',
                  backgroundColor: '#fee2e2',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  color: '#dc2626',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <AlertCircle size={16} />
                  {infographicError}
                </div>
              )}

              {/* Generated Infographic Display */}
              {generatedInfographic && (
                <div style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.75rem',
                  padding: '1rem',
                  backgroundColor: '#fafafa'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '0.75rem'
                  }}>
                    <div style={{ 
                      fontSize: '0.875rem', 
                      fontWeight: '600', 
                      color: '#374151'
                    }}>
                      Infographic
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {/* Insert Button */}
                      <button
                        onClick={insertInfographicAtCursor}
                        disabled={!isEditingContent}
                        style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: infographicCopySuccess === 'inserted' ? '#dcfce7' : (isEditingContent ? '#dbeafe' : '#f3f4f6'),
                          color: infographicCopySuccess === 'inserted' ? '#16a34a' : (isEditingContent ? '#1e40af' : '#9ca3af'),
                          border: `1px solid ${infographicCopySuccess === 'inserted' ? '#bbf7d0' : (isEditingContent ? '#93c5fd' : '#d1d5db')}`,
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          cursor: isEditingContent ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          transition: 'all 0.2s ease'
                        }}
                        title={isEditingContent ? 'Insert infographic at cursor' : 'Enter Edit Mode to insert'}
                      >
                        {infographicCopySuccess === 'inserted' ? (
                          <>
                            <CheckCircle size={12} />
                            Inserted!
                          </>
                        ) : (
                          <>
                            <Target size={12} />
                            Insert
                          </>
                        )}
                      </button>
                      {/* Copy HTML Button */}
                      <button
                        onClick={copyInfographicHtml}
                        style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: infographicCopySuccess === 'copied' ? '#dcfce7' : 'white',
                          color: infographicCopySuccess === 'copied' ? '#16a34a' : '#374151',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {infographicCopySuccess === 'copied' ? (
                          <>
                            <CheckCircle size={12} />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy size={12} />
                            Copy HTML
                          </>
                        )}
                      </button>
                      {/* Download PNG Button */}
                      <button
                        onClick={() => generatedInfographic && downloadImageAsPng(generatedInfographic)}
                        style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: imageDownloadSuccess === 'infographic' ? '#dcfce7' : 'white',
                          color: imageDownloadSuccess === 'infographic' ? '#16a34a' : '#374151',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          transition: 'all 0.2s ease'
                        }}
                        title="Download this infographic as a PNG file"
                      >
                        {imageDownloadSuccess === 'infographic' ? (
                          <>
                            <CheckCircle size={12} />
                            Saved!
                          </>
                        ) : (
                          <>
                            <Download size={12} />
                            Download
                          </>
                        )}
                      </button>
                      {/* Regenerate Button */}
                      <button
                        onClick={generateInfographic}
                        disabled={isGeneratingInfographic}
                        style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: isGeneratingInfographic ? '#fef3c7' : '#f3f4f6',
                          color: isGeneratingInfographic ? '#92400e' : '#374151',
                          border: `1px solid ${isGeneratingInfographic ? '#fcd34d' : '#e5e7eb'}`,
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          cursor: isGeneratingInfographic ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          transition: 'all 0.2s ease'
                        }}
                        title="Regenerate infographic"
                      >
                        <RefreshCw 
                          size={12} 
                          style={{ 
                            animation: isGeneratingInfographic ? 'spin 1s linear infinite' : 'none' 
                          }} 
                        />
                        {isGeneratingInfographic ? 'Regenerating...' : 'Regenerate'}
                      </button>
                      {/* Undo Button */}
                      <button
                        onClick={undoChange}
                        disabled={!isEditingContent || historyIndex <= 0}
                        style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: '#f3f4f6',
                          color: (!isEditingContent || historyIndex <= 0) ? '#9ca3af' : '#374151',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          cursor: (!isEditingContent || historyIndex <= 0) ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          transition: 'all 0.2s ease',
                          opacity: (!isEditingContent || historyIndex <= 0) ? 0.5 : 1
                        }}
                        title={historyIndex > 0 ? 'Undo last change' : 'Nothing to undo'}
                      >
                        <Undo2 size={12} />
                        Undo
                      </button>
                      {/* Redo Button */}
                      <button
                        onClick={redoChange}
                        disabled={!isEditingContent || historyIndex >= editHistory.length - 1}
                        style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: '#f3f4f6',
                          color: (!isEditingContent || historyIndex >= editHistory.length - 1) ? '#9ca3af' : '#374151',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          cursor: (!isEditingContent || historyIndex >= editHistory.length - 1) ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          transition: 'all 0.2s ease',
                          opacity: (!isEditingContent || historyIndex >= editHistory.length - 1) ? 0.5 : 1
                        }}
                        title={historyIndex < editHistory.length - 1 ? 'Redo last change' : 'Nothing to redo'}
                      >
                        <Redo2 size={12} />
                        Redo
                      </button>
                    </div>
                  </div>

                  {/* Infographic Image */}
                  <div style={{
                    borderRadius: '0.5rem',
                    overflow: 'hidden',
                    border: '1px solid #e5e7eb',
                    marginBottom: '0.75rem',
                    backgroundColor: '#f3f4f6'
                  }}>
                    <img 
                      src={generatedInfographic.imageUrl || `data:${generatedInfographic.mimeType};base64,${generatedInfographic.base64Data}`} 
                      alt={generatedInfographic.altText}
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block'
                      }}
                    />
                  </div>
                  
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: '#6b7280', 
                    fontStyle: 'italic',
                    lineHeight: '1.4'
                  }}>
                    "{generatedInfographic.altText}"
                  </div>
                </div>
              )}
            </div>

            {/* Right Panel - Generated Content Display */}
            <div ref={rightPanelRef} className="content-modal-panel" style={{ 
              position: 'relative',
              padding: '1.5rem',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 className="mobile-only-heading" style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', margin: 0, marginBottom: '1rem' }}>
                  Generated Content
                </h3>
                
                {/* Clear Button - Centered */}
                {generatedContent && (
                  <button
                    onClick={clearGeneratedContent}
                    className="content-modal-btn"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: '#fef2f2',
                      border: '0.0625rem solid #fecaca',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      fontWeight: '500',
                      color: '#dc2626',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#dc2626';
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.borderColor = '#dc2626';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#fef2f2';
                      e.currentTarget.style.color = '#dc2626';
                      e.currentTarget.style.borderColor = '#fecaca';
                    }}
                  >
                    <X size={14} />
                    Clear Generated Content
                  </button>
                )}
              </div>

              {/* Action Buttons Row - Exactly like ContentCreationModal */}
              {generatedContent && (
                <div className="content-action-buttons" style={{ 
                  display: 'flex', 
                  gap: '0.75rem', 
                  marginBottom: '1.5rem',
                  flexWrap: 'wrap',
                  justifyContent: 'center'
                }}>
                  {/* Edit/Save Button */}
                  <button
                    onClick={toggleEditMode}
                    className="content-modal-btn mobile-hide-edit"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1rem',
                      backgroundColor: isEditingContent ? '#10b981' : '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      minHeight: '2.75rem',
                      minWidth: '7.5rem',
                      justifyContent: 'center'
                    }}
                  >
                    {isEditingContent ? (
                      <>
                        <Check size={14} />
                        Save Changes
                      </>
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="m18.5 2.5 a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                        Edit Content
                      </>
                    )}
                  </button>

                  {/* Publish to CMS Button */}
                  <button
                    onClick={() => setShowCMSModal(true)}
                    className="content-modal-btn"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1rem',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      minHeight: '2.75rem',
                      minWidth: '7.5rem',
                      justifyContent: 'center'
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#059669')}
                    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#10b981')}
                  >
                    <Globe size={14} />
                    Publish to CMS
                  </button>

                  {/* Open in HTML Button */}
                  <button
                    onClick={openInHTML}
                    className="content-modal-btn"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1rem',
                      backgroundColor: '#7c3aed',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      minHeight: '2.75rem',
                      minWidth: '7.5rem',
                      justifyContent: 'center'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#6d28d9';
                      e.currentTarget.style.transform = 'translateY(-0.0625rem)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#7c3aed';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m6 16 6-12 6 12H6Z"/>
                      <path d="m8 12 8 0"/>
                    </svg>
                    Open in HTML
                  </button>

                  <button
                    onClick={openGoogleDocs}
                    className="content-modal-btn"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1rem',
                      backgroundColor: '#84ADEA',
                      color: 'black',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      minHeight: '2.75rem', // Touch-friendly
                      minWidth: '12.5rem',
                      justifyContent: 'center'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#6b96e8';
                      e.currentTarget.style.color = 'black';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#84ADEA';
                      e.currentTarget.style.color = 'black';
                    }}
                  >
                    <img 
                      src="/google-docs-logo.png" 
                      alt="Google Docs"
                      style={{
                        width: '1rem',
                        height: '1rem',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        // Fallback to ExternalLink icon if logo fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          const icon = document.createElement('div');
                          icon.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m7 7 10 10-5 0 0-5"/><path d="m17 7-10 10"/></svg>';
                          icon.style.display = 'flex';
                          icon.style.alignItems = 'center';
                          icon.style.justifyContent = 'center';
                          parent.insertBefore(icon, target);
                        }
                      }}
                    />
                    Google Docs
                  </button>

                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={openGoogleSheets}
                      className="content-modal-btn"
                      disabled={isOpeningSheets}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 1rem',
                        backgroundColor: '#16a34a',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: isOpeningSheets ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        minHeight: '2.75rem', // Touch-friendly
                        minWidth: '12.5rem',
                        justifyContent: 'center',
                        opacity: isOpeningSheets ? 0.6 : 1
                      }}
                      onMouseOver={(e) => {
                        if (!isOpeningSheets) {
                          e.currentTarget.style.backgroundColor = '#15803d';
                          e.currentTarget.style.transform = 'translateY(-0.0625rem)';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!isOpeningSheets) {
                          e.currentTarget.style.backgroundColor = '#16a34a';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      {isOpeningSheets ? (
                        <Clock className="animate-spin" size={14} />
                      ) : (
                        <Table size={14} />
                      )}
                      {isOpeningSheets ? 'Logging to Sheets...' : 'Open in Google Sheets'}
                    </button>
                    
                    {/* Success message */}
                    {showSheetsMessage && (
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '100%',
                        transform: 'translate(0.5rem, -50%)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        padding: '0.5rem 0.75rem',
                        backgroundColor: '#10b981',
                        color: 'white',
                        borderRadius: '0.375rem',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)',
                        zIndex: 1000
                      }}>
                        <Check style={{ width: '0.875rem', height: '0.875rem' }} />
                        {sheetsSuccessMessage}
                      </div>
                    )}
                  </div>

                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={copyToClipboard}
                      className="content-modal-btn"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 1rem',
                        backgroundColor: '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        minHeight: '2.75rem',
                        minWidth: '7.5rem',
                        justifyContent: 'center'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = '#4b5563';
                        e.currentTarget.style.transform = 'translateY(-0.0625rem)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#6b7280';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <Copy size={14} />
                      Copy
                    </button>
                    
                    {/* Copied message */}
                    {showCopiedMessage && (
                      <div style={{
                        position: 'absolute',
                        top: '3rem',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        padding: '0.5rem 0.75rem',
                        backgroundColor: '#10b981',
                        color: 'white',
                        borderRadius: '0.375rem',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)',
                        zIndex: 1000
                      }}>
                        <Check style={{ width: '0.875rem', height: '0.875rem' }} />
                        Copied!
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Generated Content Display */}
              {generatedContent ? (
                <div
                  className="generated-content-display"
                  style={{
                    border: '0.0625rem solid #e5e7eb',
                    borderRadius: '0.75rem',
                    padding: '2rem',
                    minHeight: '25rem',
                    backgroundColor: 'white',
                    boxShadow: '0 0.0625 0.1875rem rgba(0, 0, 0, 0.1)'
                  }}
                >
                  {/* Meta SEO Fields - Show at the top */}

                  {(metaSeoTitle || metaDescription) && (
                    <div style={{ marginBottom: '2rem' }}>
                      {metaSeoTitle && (
                        <div style={{ marginBottom: '1rem', position: 'relative' }}>
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong>Meta SEO Title:</strong>
                            <button
                              onClick={copyMetaTitle}
                              style={{
                                marginLeft: '0.5rem',
                                padding: '0.25rem 0.5rem',
                                fontSize: '0.75rem',
                                backgroundColor: '#f3f4f6',
                                border: '0.0625rem solid #10b981',
                                borderRadius: '0.375rem',
                                cursor: 'pointer',
                                color: '#10b981'
                              }}
                            >
                              Copy
                            </button>
                            
                            {/* Copied message for meta title */}
                            {showMetaTitleCopied && (
                              <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '100%',
                                transform: 'translate(0.5rem, -50%)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                padding: '0.5rem 0.75rem',
                                backgroundColor: '#10b981',
                                color: 'white',
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                whiteSpace: 'nowrap',
                                boxShadow: '0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)',
                                zIndex: 1000
                              }}>
                                <Check style={{ width: '0.875rem', height: '0.875rem' }} />
                                Copied!
                              </div>
                            )}
                          </div>
                          <div style={{ 
                            padding: '0.75rem', 
                            backgroundColor: '#f9fafb', 
                            borderRadius: '0.375rem', 
                            border: '1px solid #e5e7eb', 
                            wordWrap: 'break-word', 
                            whiteSpace: 'pre-wrap',
                            lineHeight: '1.5'
                          }}>
                            {metaSeoTitle}
                          </div>
                        </div>
                      )}
                      {metaDescription && (
                        <div style={{ marginBottom: '1rem', position: 'relative' }}>
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong>Meta Description:</strong>
                            <button
                              onClick={copyMetaDescription}
                              style={{
                                marginLeft: '0.5rem',
                                padding: '0.25rem 0.5rem',
                                fontSize: '0.75rem',
                                backgroundColor: '#f3f4f6',
                                border: '0.0625rem solid #10b981',
                                borderRadius: '0.375rem',
                                cursor: 'pointer',
                                color: '#10b981'
                              }}
                            >
                              Copy
                            </button>
                            
                            {/* Copied message for meta description */}
                            {showMetaDescCopied && (
                              <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '100%',
                                transform: 'translate(0.5rem, -50%)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                padding: '0.5rem 0.75rem',
                                backgroundColor: '#10b981',
                                color: 'white',
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                whiteSpace: 'nowrap',
                                boxShadow: '0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)',
                                zIndex: 1000
                              }}>
                                <Check style={{ width: '0.875rem', height: '0.875rem' }} />
                                Copied!
                              </div>
                            )}
                          </div>
                          <div style={{ 
                            padding: '0.75rem', 
                            backgroundColor: '#f9fafb', 
                            borderRadius: '0.375rem', 
                            border: '1px solid #e5e7eb', 
                            wordWrap: 'break-word', 
                            whiteSpace: 'pre-wrap',
                            lineHeight: '1.5'
                          }}>
                            {metaDescription}
                          </div>
                        </div>
                      )}
                      <hr style={{ border: 'none', borderTop: '0.0625rem solid #e5e7eb', margin: '1.5rem 0' }} />
                    </div>
                  )}

                  {/* Main Content */}
                  {/* Edit Mode Indicator */}
                  {isEditingContent && (
                    <div style={{
                      marginBottom: '1rem',
                      padding: '0.75rem 1rem',
                      backgroundColor: '#fffbf5',
                      borderRadius: '0.5rem',
                      border: '0.125rem solid #f59e0b',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="m18.5 2.5 a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#92400e' }}>
                        ✏️ EDITING MODE - Click "Save Changes" when done
                      </span>
                    </div>
                  )}

                  {isEditingContent ? (
                    <div style={{ position: 'relative' }}>
                      {/* CTA Insertion Toolbar */}
                      {generatedCTAs && (
                        <div style={{
                          marginBottom: '1rem',
                          padding: '1rem',
                          backgroundColor: '#f0fdf4',
                          border: '1px solid #bbf7d0',
                          borderRadius: '0.5rem'
                        }}>
                          <div style={{ 
                            fontSize: '0.875rem', 
                            fontWeight: '600', 
                            color: '#16a34a',
                            marginBottom: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <Target size={16} />
                            Insert CTA at Cursor Position
                          </div>
                          
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: '#374151',
                            marginBottom: '0.75rem',
                            fontStyle: 'italic'
                          }}>
                            Click in the text editor where you want to insert a CTA, then click one of the buttons below:
                          </div>

                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                            {Object.entries(generatedCTAs.cta_variants).map(([position, ctaData]) => (
                              <button
                                key={position}
                                onClick={() => insertCTAAtCursor(position as 'beginning' | 'middle' | 'end')}
                                style={{
                                  padding: '0.5rem 1rem',
                                  backgroundColor: ctaCopySuccess === `${position}_inserted` ? '#dcfce7' : (position === 'beginning' ? '#dbeafe' : position === 'middle' ? '#fef3c7' : '#dcfce7'),
                                  color: ctaCopySuccess === `${position}_inserted` ? '#16a34a' : (position === 'beginning' ? '#1e40af' : position === 'middle' ? '#b45309' : '#16a34a'),
                                  border: `1px solid ${ctaCopySuccess === `${position}_inserted` ? '#bbf7d0' : (position === 'beginning' ? '#93c5fd' : position === 'middle' ? '#fcd34d' : '#bbf7d0')}`,
                                  borderRadius: '0.375rem',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.375rem',
                                  textTransform: 'uppercase',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                                title={`Insert ${position} CTA: ${ctaData.cta.headline}`}
                              >
                                {ctaCopySuccess === `${position}_inserted` ? (
                                  <>
                                    <CheckCircle size={12} />
                                    Inserted!
                                  </>
                                ) : (
                                  <>
                                    <Target size={12} />
                                    Insert {position === 'end' ? 'ending' : position} CTA
                                  </>
                                )}
                              </button>
                            ))}
                            
                            {/* CTA Count Display */}
                            {countInsertedCTAs() > 0 && (
                              <div style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: '#f0fdf4',
                                border: '1px solid #bbf7d0',
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                color: '#16a34a',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem'
                              }}>
                                <CheckCircle size={12} />
                                {countInsertedCTAs()} CTA{countInsertedCTAs() !== 1 ? 's' : ''} inserted
                              </div>
                            )}
                          </div>

                          {/* Content Management Controls */}
                          <div style={{ 
                            display: 'flex', 
                            gap: '0.5rem', 
                            marginTop: '0.75rem', 
                            paddingTop: '0.75rem',
                            borderTop: '1px solid #e5e7eb',
                            flexWrap: 'wrap',
                            alignItems: 'center'
                          }}>
                            {/* Clear All CTAs Button */}
                            <button
                              onClick={clearAllCTAs}
                              disabled={countInsertedCTAs() === 0}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: countInsertedCTAs() > 0 ? '#fee2e2' : '#f3f4f6',
                                color: countInsertedCTAs() > 0 ? '#dc2626' : '#9ca3af',
                                border: `1px solid ${countInsertedCTAs() > 0 ? '#fecaca' : '#d1d5db'}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                cursor: countInsertedCTAs() > 0 ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                if (countInsertedCTAs() > 0) {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.backgroundColor = '#fecaca';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (countInsertedCTAs() > 0) {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.backgroundColor = '#fee2e2';
                                }
                              }}
                              title={countInsertedCTAs() > 0 ? 'Remove all inserted CTAs' : 'No CTAs to clear'}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c0-1 1-2 2-2v2"/>
                                <line x1="10" y1="11" x2="10" y2="17"/>
                                <line x1="14" y1="11" x2="14" y2="17"/>
                              </svg>
                              Clear All CTAs
                            </button>

                            {/* Undo Button */}
                            <button
                              onClick={undoChange}
                              disabled={historyIndex <= 0}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: historyIndex > 0 ? '#eff6ff' : '#f3f4f6',
                                color: historyIndex > 0 ? '#2563eb' : '#9ca3af',
                                border: `1px solid ${historyIndex > 0 ? '#bfdbfe' : '#d1d5db'}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                cursor: historyIndex > 0 ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                if (historyIndex > 0) {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.backgroundColor = '#dbeafe';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (historyIndex > 0) {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.backgroundColor = '#eff6ff';
                                }
                              }}
                              title={historyIndex > 0 ? 'Undo last change' : 'Nothing to undo'}
                            >
                              <RotateCcw size={12} />
                              Undo
                            </button>

                            {/* Redo Button */}
                            <button
                              onClick={redoChange}
                              disabled={historyIndex >= editHistory.length - 1}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: historyIndex < editHistory.length - 1 ? '#f0f9ff' : '#f3f4f6',
                                color: historyIndex < editHistory.length - 1 ? '#0ea5e9' : '#9ca3af',
                                border: `1px solid ${historyIndex < editHistory.length - 1 ? '#bae6fd' : '#d1d5db'}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                cursor: historyIndex < editHistory.length - 1 ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                if (historyIndex < editHistory.length - 1) {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.backgroundColor = '#e0f2fe';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (historyIndex < editHistory.length - 1) {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.backgroundColor = '#f0f9ff';
                                }
                              }}
                              title={historyIndex < editHistory.length - 1 ? 'Redo last undone change' : 'Nothing to redo'}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" transform="scale(-1, 1)">
                                <path d="M1 4v6h6"/>
                                <path d="m1 10 18-5v12"/>
                              </svg>
                              Redo
                            </button>
                          </div>

                                                        <div style={{ 
                                fontSize: '0.625rem', 
                                color: '#6b7280',
                                marginTop: '0.5rem',
                                fontStyle: 'italic'
                              }}>
                                💡 CTAs will be inserted as fully styled quiz promotions with immediate visual feedback.
                                <br />
                                🔗 Links will point to: uscitrusnursery.com/pages/quiz with UTM tracking
                              </div>
                        </div>
                      )}

                    <textarea
                      ref={editableContentRef}
                      value={editableContent}
                      onChange={(e) => {
                        setEditableContent(e.target.value);
                        
                        // Auto-save edited content with debouncing
                        if (autoSaveTimeout) {
                          clearTimeout(autoSaveTimeout);
                        }
                        const timeout = setTimeout(() => {
                          autoSaveContent(e.target.value, metaSeoTitle, metaDescription);
                        }, 1000);
                        setAutoSaveTimeout(timeout);

                        // Save to history with debouncing (longer delay for history)
                        if (historySaveTimeout) {
                          clearTimeout(historySaveTimeout);
                        }
                        const historyTimeout = setTimeout(() => {
                          saveToHistory(e.target.value);
                        }, 2000); // 2 second delay for history saves
                        setHistorySaveTimeout(historyTimeout);
                      }}
                        placeholder="Edit your content here... Click where you want to insert a CTA, then use the buttons above."
                      style={{
                        width: '100%',
                        minHeight: '37.5rem',
                        padding: '1rem 1.25rem',
                          border: generatedCTAs ? '0.125rem solid #16a34a' : '0.125rem solid #f59e0b',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                          backgroundColor: generatedCTAs ? '#f0fdf4' : '#fffbf5',
                        transition: 'all 0.2s ease',
                        outline: 'none',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                        lineHeight: '1.6',
                        color: '#374151'
                      }}
                        onFocus={(e) => e.target.style.borderColor = generatedCTAs ? '#059669' : '#d97706'}
                        onBlur={(e) => e.target.style.borderColor = generatedCTAs ? '#16a34a' : '#f59e0b'}
                    />
                    
                    {/* Bottom CTA Controls - Duplicate of Top Controls */}
                    {generatedCTAs && (
                        <div style={{ 
                          padding: '1rem',
                          marginTop: '1rem',
                          backgroundColor: '#f0fdf4',
                          border: '1px solid #bbf7d0',
                          borderRadius: '0.5rem'
                        }}>
                          <div style={{ 
                            fontSize: '0.875rem', 
                            fontWeight: '600', 
                            color: '#16a34a',
                            marginBottom: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <Target size={16} />
                            Insert CTA at Cursor Position
                          </div>
                          
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: '#374151',
                            marginBottom: '0.75rem',
                            fontStyle: 'italic'
                          }}>
                            Click in the text editor where you want to insert a CTA, then click one of the buttons below:
                          </div>

                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                            {Object.entries(generatedCTAs.cta_variants).map(([position, ctaData]) => (
                              <button
                                key={position}
                                onClick={() => insertCTAAtCursor(position as 'beginning' | 'middle' | 'end')}
                                style={{
                                  padding: '0.5rem 1rem',
                                  backgroundColor: ctaCopySuccess === `${position}_inserted` ? '#dcfce7' : (position === 'beginning' ? '#dbeafe' : position === 'middle' ? '#fef3c7' : '#dcfce7'),
                                  color: ctaCopySuccess === `${position}_inserted` ? '#16a34a' : (position === 'beginning' ? '#1e40af' : position === 'middle' ? '#b45309' : '#16a34a'),
                                  border: `1px solid ${ctaCopySuccess === `${position}_inserted` ? '#bbf7d0' : (position === 'beginning' ? '#93c5fd' : position === 'middle' ? '#fcd34d' : '#bbf7d0')}`,
                                  borderRadius: '0.375rem',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.375rem',
                                  textTransform: 'uppercase',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                                title={`Insert ${position} CTA: ${ctaData.cta.headline}`}
                              >
                                {ctaCopySuccess === `${position}_inserted` ? (
                                  <>
                                    <CheckCircle size={12} />
                                    Inserted!
                                  </>
                                ) : (
                                  <>
                                    <Target size={12} />
                                    Insert {position === 'end' ? 'ending' : position} CTA
                                  </>
                                )}
                              </button>
                            ))}
                            
                            {/* CTA Count Display */}
                            {countInsertedCTAs() > 0 && (
                              <div style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: '#f0fdf4',
                                border: '1px solid #bbf7d0',
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                color: '#16a34a',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem'
                              }}>
                                <CheckCircle size={12} />
                                {countInsertedCTAs()} CTA{countInsertedCTAs() !== 1 ? 's' : ''} inserted
                              </div>
                            )}
                          </div>

                          {/* Content Management Controls */}
                          <div style={{ 
                            display: 'flex', 
                            gap: '0.5rem', 
                            marginTop: '0.75rem', 
                            paddingTop: '0.75rem',
                            borderTop: '1px solid #e5e7eb',
                            flexWrap: 'wrap',
                            alignItems: 'center'
                          }}>
                            {/* Clear All CTAs Button */}
                            <button
                              onClick={clearAllCTAs}
                              disabled={countInsertedCTAs() === 0}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: countInsertedCTAs() > 0 ? '#fee2e2' : '#f3f4f6',
                                color: countInsertedCTAs() > 0 ? '#dc2626' : '#9ca3af',
                                border: `1px solid ${countInsertedCTAs() > 0 ? '#fecaca' : '#d1d5db'}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                cursor: countInsertedCTAs() > 0 ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                if (countInsertedCTAs() > 0) {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.backgroundColor = '#fecaca';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (countInsertedCTAs() > 0) {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.backgroundColor = '#fee2e2';
                                }
                              }}
                              title={countInsertedCTAs() > 0 ? 'Remove all inserted CTAs' : 'No CTAs to clear'}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c0-1 1-2 2-2v2"/>
                                <line x1="10" y1="11" x2="10" y2="17"/>
                                <line x1="14" y1="11" x2="14" y2="17"/>
                              </svg>
                              Clear All CTAs
                            </button>

                            {/* Undo Button */}
                            <button
                              onClick={undoChange}
                              disabled={historyIndex <= 0}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: historyIndex > 0 ? '#eff6ff' : '#f3f4f6',
                                color: historyIndex > 0 ? '#2563eb' : '#9ca3af',
                                border: `1px solid ${historyIndex > 0 ? '#bfdbfe' : '#d1d5db'}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                cursor: historyIndex > 0 ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                if (historyIndex > 0) {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.backgroundColor = '#dbeafe';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (historyIndex > 0) {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.backgroundColor = '#eff6ff';
                                }
                              }}
                              title={historyIndex > 0 ? 'Undo last change' : 'Nothing to undo'}
                            >
                              <RotateCcw size={12} />
                              Undo
                            </button>

                            {/* Redo Button */}
                            <button
                              onClick={redoChange}
                              disabled={historyIndex >= editHistory.length - 1}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: historyIndex < editHistory.length - 1 ? '#f0f9ff' : '#f3f4f6',
                                color: historyIndex < editHistory.length - 1 ? '#0ea5e9' : '#9ca3af',
                                border: `1px solid ${historyIndex < editHistory.length - 1 ? '#bae6fd' : '#d1d5db'}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                cursor: historyIndex < editHistory.length - 1 ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                if (historyIndex < editHistory.length - 1) {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.backgroundColor = '#e0f2fe';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (historyIndex < editHistory.length - 1) {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.backgroundColor = '#f0f9ff';
                                }
                              }}
                              title={historyIndex < editHistory.length - 1 ? 'Redo last undone change' : 'Nothing to redo'}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" transform="scale(-1, 1)">
                                <path d="M1 4v6h6"/>
                                <path d="m1 10 18-5v12"/>
                              </svg>
                              Redo
                            </button>
                          </div>

                          <div style={{ 
                            fontSize: '0.625rem', 
                            color: '#6b7280',
                            marginTop: '0.5rem',
                            fontStyle: 'italic'
                          }}>
                            💡 CTAs will be inserted as fully styled quiz promotions with immediate visual feedback.
                            <br />
                            🔗 Links will point to: uscitrusnursery.com/pages/quiz with UTM tracking
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      ref={contentDisplayRef}
                      className="generated-content-display"
                      dangerouslySetInnerHTML={{ __html: sanitizeHTMLForDisplay(getDisplayContent()) }}
                      key={`display-${isEditingContent}-${editableContent.length}`}
                    />
                  )}
                </div>
              ) : (
                <div className="content-placeholder-section" style={{
                  border: '0.125rem dashed #d1d5db',
                  borderRadius: '0.75rem',
                  padding: '4rem 2rem',
                  textAlign: 'center',
                  color: '#6b7280',
                  minHeight: '25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'white'
                }}>
                  <div>
                    <Wand2 size={48} style={{ margin: '0 auto 1rem', opacity: 0.3, display: 'block' }} />
                    <p style={{ fontSize: '1.125rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                      No content generated yet
                    </p>
                    <p style={{ margin: 0 }}>
                      Complete the workflow first to see generated content here
                    </p>
                  </div>
                </div>
              )}

              {/* Bottom Action Buttons - Duplicate for convenience */}
              {generatedContent && (
                <div style={{ 
                  marginTop: '2rem',
                  paddingTop: '1rem',
                  borderTop: '0.0625rem solid #e5e7eb'
                }}>
                  <div className="content-action-buttons" style={{ 
                    display: 'flex', 
                    gap: '0.75rem',
                    marginBottom: '1rem',
                    flexWrap: 'wrap', // Stack on mobile
                    justifyContent: 'center' // Center the buttons
                  }}>
                    {/* Edit/Save Button */}
                    <button
                      onClick={toggleEditMode}
                      className="mobile-hide-edit"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 1rem',
                        backgroundColor: isEditingContent ? '#10b981' : '#f59e0b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        minHeight: '2.75rem',
                        minWidth: '7.5rem',
                        justifyContent: 'center'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = isEditingContent ? '#059669' : '#d97706';
                        e.currentTarget.style.transform = 'translateY(-0.0625rem)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = isEditingContent ? '#10b981' : '#f59e0b';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      {isEditingContent ? (
                        <>
                          <Check size={14} />
                          Save Changes
                        </>
                      ) : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="m18.5 2.5 a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                          Edit Content
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => setShowCMSModal(true)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 1rem',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        minHeight: '2.75rem',
                        minWidth: '7.5rem',
                        justifyContent: 'center'
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#059669')}
                      onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#10b981')}
                    >
                      <Globe size={14} />
                      Publish to CMS
                    </button>

                    {/* Open in HTML Button */}
                    <button
                      onClick={openInHTML}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 1rem',
                        backgroundColor: '#7c3aed',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        minHeight: '2.75rem',
                        minWidth: '7.5rem',
                        justifyContent: 'center'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = '#6d28d9';
                        e.currentTarget.style.transform = 'translateY(-0.0625rem)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#7c3aed';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m6 16 6-12 6 12H6Z"/>
                        <path d="m8 12 8 0"/>
                      </svg>
                      Open in HTML
                    </button>

                    <button
                      onClick={openGoogleDocs}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 1rem',
                        backgroundColor: '#84ADEA',
                        color: 'black',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        minHeight: '2.75rem',
                        minWidth: '12.5rem',
                        justifyContent: 'center'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = '#6b96e8';
                        e.currentTarget.style.color = 'black';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#84ADEA';
                        e.currentTarget.style.color = 'black';
                      }}
                    >
                      <img 
                        src="/google-docs-logo.png" 
                        alt="Google Docs"
                        style={{
                          width: '1rem',
                          height: '1rem',
                          objectFit: 'contain'
                        }}
                        onError={(e) => {
                          // Fallback to ExternalLink icon if logo fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            const icon = document.createElement('div');
                            icon.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m7 7 10 10-5 0 0-5"/><path d="m17 7-10 10"/></svg>';
                            icon.style.display = 'flex';
                            icon.style.alignItems = 'center';
                            icon.style.justifyContent = 'center';
                            parent.insertBefore(icon, target);
                          }
                        }}
                      />
                      Google Docs
                    </button>

                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={openGoogleSheets}
                        disabled={isOpeningSheets}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.75rem 1rem',
                          backgroundColor: isOpeningSheets ? '#9ca3af' : '#16a34a',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.5rem',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          cursor: isOpeningSheets ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease',
                          minHeight: '2.75rem',
                          minWidth: '12.5rem',
                          justifyContent: 'center',
                          opacity: isOpeningSheets ? 0.6 : 1
                        }}
                        onMouseOver={(e) => {
                          if (!isOpeningSheets) {
                            e.currentTarget.style.backgroundColor = '#15803d';
                            e.currentTarget.style.transform = 'translateY(-0.0625rem)';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (!isOpeningSheets) {
                            e.currentTarget.style.backgroundColor = '#16a34a';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }
                        }}
                      >
                        {isOpeningSheets ? (
                          <Clock className="animate-spin" size={14} />
                        ) : (
                          <Table size={14} />
                        )}
                        {isOpeningSheets ? 'Logging to Sheets...' : 'Open in Google Sheets'}
                      </button>
                      
                      {/* Success message */}
                      {showSheetsMessage && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '100%',
                          transform: 'translate(0.5rem, -50%)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.375rem',
                          padding: '0.5rem 0.75rem',
                          backgroundColor: '#10b981',
                          color: 'white',
                          borderRadius: '0.375rem',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          whiteSpace: 'nowrap',
                          boxShadow: '0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)',
                          zIndex: 1000
                        }}>
                          <Check style={{ width: '0.875rem', height: '0.875rem' }} />
                          {sheetsSuccessMessage}
                        </div>
                      )}
                    </div>

                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={copyToClipboard}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.75rem 1rem',
                          backgroundColor: '#6b7280',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.5rem',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          minHeight: '2.75rem',
                          minWidth: '7.5rem',
                          justifyContent: 'center'
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#4b5563')}
                        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#6b7280')}
                      >
                        <Copy size={14} />
                        Copy
                      </button>
                      
                      {/* Copied message */}
                      {showCopiedMessage && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '100%',
                          transform: 'translate(0.5rem, -50%)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.375rem',
                          padding: '0.5rem 0.75rem',
                          backgroundColor: '#10b981',
                          color: 'white',
                          borderRadius: '0.375rem',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          whiteSpace: 'nowrap',
                          boxShadow: '0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)',
                          zIndex: 1000
                        }}>
                          <Check style={{ width: '0.875rem', height: '0.875rem' }} />
                          Copied!
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Clear Button - Full width on mobile */}
                  <button
                    onClick={clearGeneratedContent}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1rem',
                      backgroundColor: '#fef2f2',
                      border: '0.0625rem solid #fecaca',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#dc2626',
                      transition: 'all 0.2s ease',
                      minHeight: '2.75rem',
                      width: '100%',
                      justifyContent: 'center'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#dc2626';
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.borderColor = '#dc2626';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#fef2f2';
                      e.currentTarget.style.color = '#dc2626';
                      e.currentTarget.style.borderColor = '#fecaca';
                    }}
                  >
                    <X size={14} />
                    Clear Generated Content
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Clear Content Confirmation Modal */}
      {showClearConfirmation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '25rem',
            width: '90%',
            boxShadow: '0 1.25rem 1.5625rem -0.3125rem rgba(0, 0, 0, 0.1), 0 0.625rem 0.625rem -0.3125rem rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                width: '3rem',
                height: '3rem',
                borderRadius: '50%',
                backgroundColor: '#fee2e2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <X size={24} style={{ color: '#dc2626' }} />
              </div>
              <div>
                <h3 style={{
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  color: '#374151',
                  margin: 0
                }}>
                  Clear Generated Content?
                </h3>
              </div>
            </div>
            
            <p style={{
              fontSize: '0.875rem',
              color: '#6b7280',
              lineHeight: '1.5',
              margin: '0 0 1.5rem 0'
            }}>
              This will permanently delete the generated content, meta SEO title, and meta description. This action cannot be undone. Are you sure you want to continue?
            </p>
            
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={cancelClearContent}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'white',
                  border: '0.0625rem solid #d1d5db',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                }}
              >
                Cancel
              </button>
              
              <button
                onClick={confirmClearContent}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: '#dc2626',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#b91c1c';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#dc2626';
                }}
              >
                <X size={14} />
                Yes, Clear Content
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CMS Integration Modal Component */}
      {showCMSModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '31.25rem',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>Publish Content to CMS</h3>
              <button
                onClick={() => setShowCMSModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ position: 'relative' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {[
                  { name: 'Webflow', description: 'Publish directly to your Webflow site', logo: '/webflow-logo.png' },
                  { name: 'Strapi', description: 'Add to your Strapi content library', logo: '/strapi-logo.png' },
                  { name: 'Contentful', description: 'Create entry in Contentful space', logo: '/contenful-logo.png' },
                  { name: 'Sanity', description: 'Publish to Sanity Studio', logo: '/sanity-logo.png' },
                  { name: 'WordPress', description: 'Create WordPress post/page', logo: '/wordpress-logo.png' },
                  { name: 'Custom', description: 'Configure your own API endpoint', logo: null }
                ].map((cms) => (
                  <div
                    key={cms.name}
                    style={{
                      border: '0.0625rem solid #e5e7eb',
                      borderRadius: '0.75rem',
                      padding: '1.25rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'white',
                      boxShadow: '0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05)'
                    }}
                    onClick={() => {
                      if (cms.name === 'Custom') {
                        setShowCustomCMSForm(true);
                        setShowCMSModal(false);
                      } else {
                        setShowComingSoonMessage(cms.name);
                        setTimeout(() => setShowComingSoonMessage(null), 3000);
                      }
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                      e.currentTarget.style.borderColor = '#d1d5db';
                      e.currentTarget.style.boxShadow = '0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)';
                      e.currentTarget.style.transform = 'translateY(-0.0625rem)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = '0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '1rem',
                      marginBottom: '0.5rem'
                    }}>
                      {cms.logo ? (
                        <div style={{
                          width: '2rem',
                          height: '2rem',
                          borderRadius: '0.5rem',
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#f8fafc',
                          border: '0.0625rem solid #f1f5f9',
                          flexShrink: 0
                        }}>
                          <img 
                            src={cms.logo} 
                            alt={`${cms.name} logo`}
                            style={{
                              width: '1.5rem',
                              height: '1.5rem',
                              objectFit: 'contain'
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent) {
                                parent.innerHTML = `<span style="font-size: 0.75rem; font-weight: 600; color: #64748b;">${cms.name.charAt(0)}</span>`;
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <div style={{ 
                          width: '2rem',
                          height: '2rem',
                          borderRadius: '0.5rem',
                          backgroundColor: '#6366f1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <span style={{
                            color: 'white',
                            fontSize: '0.875rem',
                            fontWeight: '600'
                          }}>
                            {cms.name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <h4 style={{ 
                        fontWeight: '600', 
                        margin: 0,
                        fontSize: '1rem',
                        color: '#111827'
                      }}>
                        {cms.name}
                      </h4>
                    </div>
                    <p style={{ 
                      color: '#6b7280', 
                      fontSize: '0.875rem', 
                      margin: 0,
                      paddingLeft: '3rem',
                      lineHeight: '1.5'
                    }}>
                      {cms.description}
                    </p>
                  </div>
                ))}
              </div>

              {/* Coming Soon Message */}
              {showComingSoonMessage && (
                <div style={{ 
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '1.5rem 2rem',
                  backgroundColor: '#EBF212',
                  color: 'black',
                  borderRadius: '0.75rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  boxShadow: '0 0.625rem 1.5625rem -0.1875rem rgba(0, 0, 0, 0.1), 0 0.25rem 0.375rem -0.125rem rgba(0, 0, 0, 0.05)',
                  zIndex: 10002,
                  minWidth: '15.625rem',
                  textAlign: 'center', 
                  animation: 'fadeInScale 0.2s ease-out'
                }}>
                  {showComingSoonMessage} integration coming soon!
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom CMS Form Modal */}
      {showCustomCMSForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '31.25rem',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>
                🧈 Custom CMS Integration (Demo)
              </h3>
              <button
                onClick={() => {
                  setShowCustomCMSForm(false);
                  setPublishResult(null);
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>

            {!publishResult ? (
              <>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                    CMS Type
                  </label>
                  <input
                    type="text"
                    value={customCMSConfig.cms_type}
                    onChange={(e) => setCustomCMSConfig({...customCMSConfig, cms_type: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '0.0625rem solid #d1d5db',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                    placeholder="e.g., Butter CMS, WordPress, Custom"
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                    API Endpoint
                  </label>
                  <input
                    type="text"
                    value={customCMSConfig.api_endpoint}
                    onChange={(e) => setCustomCMSConfig({...customCMSConfig, api_endpoint: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '0.0625rem solid #d1d5db',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                    placeholder="https://api.buttercms.com/v2"
                  />
                </div>

                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                    API Key
                  </label>
                  <input
                    type="text"
                    value={customCMSConfig.api_key}
                    onChange={(e) => setCustomCMSConfig({...customCMSConfig, api_key: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '0.0625rem solid #d1d5db',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                    placeholder="demo-api-key-12345"
                  />
                </div>

                <div style={{ 
                  display: 'flex', 
                  gap: '1rem',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    onClick={() => publishToCustomCMS('draft')}
                    disabled={isPublishing}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontWeight: '500',
                      cursor: isPublishing ? 'not-allowed' : 'pointer',
                      opacity: isPublishing ? 0.6 : 1
                    }}
                  >
                    {isPublishing ? 'Publishing...' : 'Save as Draft'}
                  </button>
                  
                  <button
                    onClick={() => publishToCustomCMS('published')}
                    disabled={isPublishing}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontWeight: '500',
                      cursor: isPublishing ? 'not-allowed' : 'pointer',
                      opacity: isPublishing ? 0.6 : 1
                    }}
                  >
                    {isPublishing ? 'Publishing...' : 'Publish Live'}
                  </button>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center' }}>
                {publishResult.success ? (
                  <div>
                    <div style={{ 
                      fontSize: '3rem', 
                      marginBottom: '1rem',
                      color: '#10b981'
                    }}>
                      ✅
                    </div>
                    <h4 style={{ 
                      color: '#10b981', 
                      marginBottom: '1rem',
                      fontSize: '1.25rem',
                      fontWeight: '600'
                    }}>
                      Content Published Successfully!
                    </h4>
                    {publishResult.demo_mode && (
                      <div style={{ 
                        backgroundColor: '#fef3c7', 
                        padding: '1rem', 
                        borderRadius: '0.5rem',
                        marginBottom: '1.5rem',
                        border: '0.0625rem solid #f59e0b'
                      }}>
                        <p style={{ 
                          color: '#92400e', 
                          margin: 0,
                          fontSize: '0.875rem',
                          fontWeight: '500'
                        }}>
                          🧈 Demo Mode: This is a simulated CMS publish for demonstration purposes.
                        </p>
                      </div>
                    )}
                    <div style={{ 
                      backgroundColor: '#f9fafb', 
                      padding: '1.5rem', 
                      borderRadius: '0.5rem',
                      textAlign: 'left',
                      marginBottom: '1.5rem'
                    }}>
                      <h5 style={{ margin: '0 0 1rem 0', color: '#374151', fontSize: '1rem' }}>Publication Details:</h5>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        <p style={{ margin: '0.5rem 0' }}><strong>CMS:</strong> {publishResult.cms_type}</p>
                        <p style={{ margin: '0.5rem 0' }}><strong>Status:</strong> {publishResult.status}</p>
                        <p style={{ margin: '0.5rem 0' }}><strong>Article ID:</strong> {publishResult.id || publishResult.post_id}</p>
                        {publishResult.handle && (
                          <p style={{ margin: '0.5rem 0' }}><strong>Slug:</strong> {publishResult.handle}</p>
                        )}
                        {publishResult.url && (
                          <p style={{ margin: '0.5rem 0' }}>
                            <strong>Article URL:</strong> 
                            <a 
                              href={publishResult.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#3b82f6', 
                                textDecoration: 'underline',
                                marginLeft: '0.5rem'
                              }}
                            >
                              View Article →
                            </a>
                          </p>
                        )}
                        {publishResult.admin_url && (
                          <p style={{ margin: '0.5rem 0' }}>
                            <strong>Admin:</strong> 
                            <a 
                              href={publishResult.admin_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#6b7280', 
                                textDecoration: 'underline',
                                marginLeft: '0.5rem'
                              }}
                            >
                              Edit in Shopify →
                            </a>
                          </p>
                        )}
                        {publishResult.demo_mode && (
                          <p style={{ 
                            margin: '1rem 0 0.5rem 0', 
                            padding: '0.5rem', 
                            backgroundColor: '#fef3c7', 
                            borderRadius: '0.25rem',
                            color: '#92400e',
                            fontSize: '0.75rem'
                          }}>
                            ⚠️ Demo Mode - Configure Shopify credentials in backend .env for real publishing
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                      {publishResult.url && !publishResult.demo_mode && (
                        <a
                          href={publishResult.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            padding: '0.75rem 2rem',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            textDecoration: 'none',
                            display: 'inline-block'
                          }}
                        >
                          View Article
                        </a>
                      )}
                      <button
                        onClick={() => {
                          setShowCustomCMSForm(false);
                          setPublishResult(null);
                        }}
                        style={{
                          padding: '0.75rem 2rem',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.5rem',
                          fontWeight: '500',
                          cursor: 'pointer'
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ 
                      fontSize: '3rem', 
                      marginBottom: '1rem',
                      color: '#dc2626'
                    }}>
                      ❌
                    </div>
                    <h4 style={{ 
                      color: '#dc2626', 
                      marginBottom: '1rem',
                      fontSize: '1.25rem',
                      fontWeight: '600'
                    }}>
                      Publication Failed
                    </h4>
                    <p style={{ 
                      color: '#6b7280', 
                      marginBottom: '2rem',
                      fontSize: '0.875rem'
                    }}>
                      {publishResult.error || 'An unknown error occurred'}
                    </p>
                    
                    <button
                      onClick={() => setPublishResult(null)}
                      style={{
                        padding: '0.75rem 2rem',
                        backgroundColor: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        marginRight: '1rem'
                      }}
                    >
                      Try Again
                    </button>
                    
                    <button
                      onClick={() => {
                        setShowCustomCMSForm(false);
                        setPublishResult(null);
                      }}
                      style={{
                        padding: '0.75rem 2rem',
                        backgroundColor: '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile backdrop for variables menu */}
      {showVariablesMenu && window.innerWidth <= 768 && (
        <div 
          className="mobile-modal-backdrop"
          onClick={() => setShowVariablesMenu(false)}
        />
      )}

      {/* Variables Menu */}
      <VariablesMenu
        showVariablesMenu={showVariablesMenu}
        variablesButtonPosition={variablesButtonPosition}
        variablesMenuRef={variablesMenuRef}
        activePromptField={activePromptField}
        variableSearchTerm={variableSearchTerm}
        setVariableSearchTerm={setVariableSearchTerm}
        setShowVariablesMenu={setShowVariablesMenu}
        brandKit={brandKit}
        insertVariable={insertVariable}
      />

      {/* Link Hover Controls */}
      <LinkHoverControls
        targetLink={targetLink}
        onRemoveLink={removeLink}
        onOpenLink={openLink}
      />
    </>
  );
};

export default BlogContentActionModal; 